import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney } from '../../utils/money';
import axios from 'axios';
import api from './useKittingApi';
import { fetchOrderwiseToken, postOrderwise } from '../../orderwiseApi';
import ManageOEMModal from '../ManageOEMModal';
import ManageSKUModal from '../ManageSKUModal';
import KitImporter from './KitImporter';
import './kitting-modal.css';

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const DEFAULT_SKU_GROUP_LABEL = 'TBA';

// A real FitmentLocation row can have an empty name; give it a visible label so a
// selected blank location isn't mistaken for "nothing selected".
const formatFitmentLocationLabel = (name) => {
  const trimmed = (name ?? '').toString().trim();
  return trimmed || '(Blank)';
};
const SKU_PRICE_FIELDS = [
  'Price',
  'UnitPrice',
  'UnitCost',
  'RetailPrice',
  'Retail',
  'DefaultPrice',
  'DefaultCost',
  'Cost',
  'TradePrice',
  'DealerPrice',
  'WebPrice',
  'vfp_vafp_rsp_exc_vat',
  'vfp_vafp_rsp_inc_vat',
  'vfp_vafp_rsp'
];

const normalizeSkuGroupLabel = (label) => {
  if (typeof label === 'string') {
    const trimmed = label.trim();
    return trimmed ? trimmed : DEFAULT_SKU_GROUP_LABEL;
  }
  if (label === null || label === undefined) {
    return DEFAULT_SKU_GROUP_LABEL;
  }
  const stringified = String(label).trim();
  return stringified || DEFAULT_SKU_GROUP_LABEL;
};

function emptyComponent() {
  return {
    KitComponentId: null,
    ComponentOEMNumber: '',
    Quantity: 1,
    OEMNote: '',
    ItemTypeKey: null,
    FitmentLocationKey: null,
    KitItemTypeId: null,
    KitFitmentLocationId: null,
    SKUGroupKey: null,
    SKUGroup: DEFAULT_SKU_GROUP_LABEL,
    SKUKey: null,
    SKU: '',
    OEMKey: null,
    SkuOptions: []
  };
}

const defaultTolerance = {
  subsetQuantityTolerance: 1,
  subsetMissingTolerance: 0
};

const COPY_MODAL_PAGE_SIZE = 15;

const createCopyModalState = () => ({
  open: false,
  search: '',
  searchDraft: '',
  page: 1,
  pageSize: COPY_MODAL_PAGE_SIZE,
  results: [],
  total: 0,
  loading: false,
  error: '',
  copyingId: null
});

const clampToRange = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  if (numeric < min) return min;
  if (numeric > max) return max;
  return numeric;
};

const normalizeTolerance = (rawTolerance) => {
  if (!rawTolerance || typeof rawTolerance !== 'object') {
    return { ...defaultTolerance };
  }
  return {
    subsetQuantityTolerance: clampToRange(
      rawTolerance.subsetQuantityTolerance ?? defaultTolerance.subsetQuantityTolerance,
      0,
      1
    ),
    subsetMissingTolerance: 0
  };
};

export default function KittingModal({
  open,
  onClose,
  initialKitId,
  fitmentContext = null,
  initialComponents = null,
  canEdit = false,
  isDark = false,
  onKitLinked,
  ensureFitment = null
}) {
  const [kitId, setKitId] = useState(initialKitId || null);
  const [kit, setKit] = useState({
    Description: '',
    SKUGroupKey: null,
    SKUGroup: DEFAULT_SKU_GROUP_LABEL,
    BaseItemTypeKey: null,
    BaseKitItemTypeId: null,
    BaseFitmentLocationKey: null,
    BaseKitFitmentLocationId: null,
    FitmentInfo: ''
  });
  const [components, setComponents] = useState([emptyComponent()]);
  const [history, setHistory] = useState([]);
  const [historySelection, setHistorySelection] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyApplyBusyId, setHistoryApplyBusyId] = useState(null);
  const [historyModalError, setHistoryModalError] = useState('');
  const [reuseCandidates, setReuseCandidates] = useState([]);
  const [kitItemTypes, setKitItemTypes] = useState([]);
  const [coreItemTypes, setCoreItemTypes] = useState([]);
  const [kitFitmentLocations, setKitFitmentLocations] = useState([]);
  const baseFitmentLocationsByItemTypeRef = useRef(new Map());
  const [baseLocationVersion, setBaseLocationVersion] = useState(0);
  const [stockSummary, setStockSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stockBusy, setStockBusy] = useState(false);
  const [tolerance, setTolerance] = useState(() => {
    try {
      const persisted = localStorage.getItem('kitTolerance');
      if (persisted) {
        const parsed = JSON.parse(persisted);
        return normalizeTolerance(parsed);
      }
    } catch {
      /* ignore */
    }
    return normalizeTolerance(defaultTolerance);
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [localFitmentKey, setLocalFitmentKey] = useState(fitmentContext?.FitmentKey ?? null);
  const [manageOEMState, setManageOEMState] = useState({ open: false, componentIndex: null, initialNumber: '', initialKey: null });
  const [manageSKUState, setManageSKUState] = useState({ open: false, componentIndex: null, initialGroup: '', initialSku: '' });

  // ── Kit-level inline SKU→SKUGroup lookup states ──
  const [kitSkuGroupSearch, setKitSkuGroupSearch] = useState('');
  const [kitSkuGroupResults, setKitSkuGroupResults] = useState([]);
  const [showKitSkuGroupDropdown, setShowKitSkuGroupDropdown] = useState(false);
  const [kitSkuGroupBusy, setKitSkuGroupBusy] = useState(false);
  const [kitSkuLookupGroups, setKitSkuLookupGroups] = useState([]);
  const [kitSkuLookupKey, setKitSkuLookupKey] = useState(null);
  const [kitSkuLookupBusy, setKitSkuLookupBusy] = useState(false);
  const [kitSkuLookupDone, setKitSkuLookupDone] = useState(false);
  const [kitCreateGroupMode, setKitCreateGroupMode] = useState(false);
  const [kitNewGroupName, setKitNewGroupName] = useState('');
  const [kitCreateGroupBusy, setKitCreateGroupBusy] = useState(false);
  const [kitCreateGroupError, setKitCreateGroupError] = useState('');
  const [kitMergeWarning, setKitMergeWarning] = useState(null);
  const kitSkuGroupNameCacheRef2 = useRef({});
  const [activeComponentIndex, setActiveComponentIndex] = useState(null);
  const oemLookupCacheRef = useRef(new Map());
  const skuGroupOptionsCacheRef = useRef(new Map());
  const skuGroupNameCacheRef = useRef(new Map());
  const autoHydratedOEMsRef = useRef(new Set());
  const hasAutoLoadedSuggestionsRef = useRef(false);
  const canModify = canEdit && !!open;
  const lastDefaultDescriptionRef = useRef('');
  const [skuOptionsModal, setSkuOptionsModal] = useState({ open: false, componentIndex: null });
  const [skuOptionsLoading, setSkuOptionsLoading] = useState(false);
  const [skuOptionsError, setSkuOptionsError] = useState('');
  const skuOptionsHydrationCacheRef = useRef(new Map());
  const [copyModalState, setCopyModalState] = useState(() => createCopyModalState());
  const [suppressInitialKitId, setSuppressInitialKitId] = useState(false);
  const copyPageSize = copyModalState.pageSize || COPY_MODAL_PAGE_SIZE;
  const [productOEMs, setProductOEMs] = useState([]);
  const [productOEMInput, setProductOEMInput] = useState('');
  const [refreshImporterKitId, setRefreshImporterKitId] = useState(null);
  const refreshImporterKitIds = useMemo(
    () => (refreshImporterKitId ? [refreshImporterKitId] : null),
    [refreshImporterKitId]
  );

  const hydrateKitDetail = useCallback((detail) => {
    if (!detail) return;
    setKit((prev) => ({
      ...prev,
      ...detail.kit,
      Description: detail?.kit?.Description ?? ''
    }));
    // Seed the inline SKU Group search field with the kit's current group name
    const groupLabel = normalizeSkuGroupLabel(
      detail?.kit?.SKUGroup || detail?.kit?.SKUGroupName || detail?.kit?.SKUGroupCode || ''
    );
    if (groupLabel && groupLabel !== DEFAULT_SKU_GROUP_LABEL) {
      setKitSkuGroupSearch(groupLabel);
    } else {
      setKitSkuGroupSearch('');
    }
    setProductOEMs(
      Array.isArray(detail?.productOEMs)
        ? detail.productOEMs.map(po => ({ OEMNumber: po.OEMNumber || '', OEMKey: po.OEMKey ?? null }))
        : []
    );
    setComponents(
      (detail?.components || []).map((comp) => ({
        ...comp,
        SkuOptions: Array.isArray(comp.SkuOptions) ? comp.SkuOptions : [],
        SKUGroup: normalizeSkuGroupLabel(comp.SKUGroup || comp.SKUGroupName || comp.SKUGroupCode || ''),
        SKU: comp.SKU || comp.SKUCode || '',
        OEMKey: comp.OEMKey ?? null
      }))
    );
  }, [setKit, setComponents]);

  const refreshHistory = useCallback(async (targetKitId, preferredAuditId = null) => {
    if (!targetKitId) {
      setHistory([]);
      setHistorySelection(null);
      return [];
    }

    const rows = await api.fetchKitHistory(targetKitId);
    setHistory(rows);

    if (!rows.length) {
      setHistorySelection(null);
      return rows;
    }

    setHistorySelection((prevSelection) => {
      const resolvedId = preferredAuditId ?? prevSelection?.KitAuditLogId ?? null;
      if (resolvedId) {
        const match = rows.find((row) => row.KitAuditLogId === resolvedId);
        if (match) {
          return prevSelection && prevSelection.KitAuditLogId === match.KitAuditLogId
            ? prevSelection
            : match;
        }
      }

      const fallback = rows[0] ?? null;
      if (fallback && prevSelection && prevSelection.KitAuditLogId === fallback.KitAuditLogId) {
        return prevSelection;
      }

      return fallback;
    });

    return rows;
  }, [setHistory, setHistorySelection]);

  const openHistoryModal = useCallback(() => {
    setHistoryModalError('');
    setHistoryModalOpen(true);
  }, [setHistoryModalError, setHistoryModalOpen]);

  const closeHistoryModal = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryModalError('');
    setHistoryApplyBusyId(null);
  }, [setHistoryModalOpen, setHistoryModalError, setHistoryApplyBusyId]);

  const renderHistoryState = useCallback((state) => {
    if (!state) {
      return (
        <div style={{ fontStyle: 'italic', color: isDark ? '#bdbdbd' : '#666' }}>
          No snapshot captured for this event.
        </div>
      );
    }
    const snapshotKit = state.kit || {};
    const snapshotComponents = Array.isArray(state.components) ? state.components : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div>
          <div><strong>Description:</strong> {snapshotKit.Description || '—'}</div>
          <div><strong>Base Item Type:</strong> {snapshotKit.BaseItemTypeKey ?? snapshotKit.BaseKitItemTypeId ?? '—'}</div>
          <div><strong>Base Location:</strong> {snapshotKit.BaseFitmentLocationKey ?? snapshotKit.BaseKitFitmentLocationId ?? '—'}</div>
          <div><strong>Component Count:</strong> {snapshotComponents.length}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', background: isDark ? '#2a2a2a' : '#f0f2f5' }}>
              <th style={{ padding: '0.4rem' }}>OEM</th>
              <th style={{ padding: '0.4rem' }}>Qty</th>
              <th style={{ padding: '0.4rem' }}>Item Type</th>
              <th style={{ padding: '0.4rem' }}>Location</th>
              <th style={{ padding: '0.4rem' }}>SKU Group</th>
              <th style={{ padding: '0.4rem' }}>SKU</th>
            </tr>
          </thead>
          <tbody>
            {snapshotComponents.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '0.5rem', fontStyle: 'italic', color: isDark ? '#bdbdbd' : '#666' }}>
                  No components recorded.
                </td>
              </tr>
            )}
            {snapshotComponents.map((comp, idx) => (
              <tr key={comp.KitComponentId ?? `comp-${idx}`} style={{ borderBottom: `1px solid ${isDark ? '#333' : '#e2e6ea'}` }}>
                <td style={{ padding: '0.4rem' }}>{comp.ComponentOEMNumber || '—'}</td>
                <td style={{ padding: '0.4rem' }}>{comp.Quantity ?? '—'}</td>
                <td style={{ padding: '0.4rem' }}>{comp.ItemTypeKey ?? comp.KitItemTypeId ?? '—'}</td>
                <td style={{ padding: '0.4rem' }}>{comp.FitmentLocationKey ?? comp.KitFitmentLocationId ?? '—'}</td>
                <td style={{ padding: '0.4rem' }}>{comp.SKUGroup || comp.SKUGroupName || comp.SKUGroupCode || '—'}</td>
                <td style={{ padding: '0.4rem' }}>{comp.SKU || comp.SKUCode || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [isDark]);

  const baseItemTypeKey = kit.BaseItemTypeKey;
  const baseKitItemTypeId = kit.BaseKitItemTypeId;
  const baseFitmentLocationKey = kit.BaseFitmentLocationKey;
  const baseKitFitmentLocationId = kit.BaseKitFitmentLocationId;

  const getComponentCacheKey = useCallback((component, index) => {
    if (component?.KitComponentId) {
      return `kit:${component.KitComponentId}`;
    }
    const oem = (component?.ComponentOEMNumber || '').toString().trim().toLowerCase();
    if (oem) {
      return `oem:${oem}:${index}`;
    }
    return `idx:${index}`;
  }, []);

  const invalidateSkuHydrationForIndex = useCallback((component, index) => {
    const cacheKey = getComponentCacheKey(component, index);
    if (cacheKey) {
      skuOptionsHydrationCacheRef.current.delete(cacheKey);
    }
  }, [getComponentCacheKey]);

  const fetchSkuGroupName = useCallback(async (groupKey) => {
    if (!groupKey) return '';
    if (skuGroupNameCacheRef.current.has(groupKey)) {
      return skuGroupNameCacheRef.current.get(groupKey);
    }
    try {
      const { data } = await axios.get(`/api/sku-groups/${groupKey}`);
      const name = data?.SKUGroup || data?.Name || '';
      skuGroupNameCacheRef.current.set(groupKey, name || '');
      return name || '';
    } catch (err) {
      console.warn('Failed to fetch SKU group name', err);
      skuGroupNameCacheRef.current.set(groupKey, '');
      return '';
    }
  }, []);

  const fetchSkuOptionsForGroup = useCallback(async (groupKey) => {
    if (!groupKey) return [];
    if (skuGroupOptionsCacheRef.current.has(groupKey)) {
      return skuGroupOptionsCacheRef.current.get(groupKey);
    }
    try {
      const { data } = await axios.get(`/api/sku-groups/${groupKey}/skus`);
      const list = Array.isArray(data) ? data : [];
      skuGroupOptionsCacheRef.current.set(groupKey, list);
      return list;
    } catch (err) {
      console.warn('Failed to fetch SKU group SKUs', err);
      skuGroupOptionsCacheRef.current.set(groupKey, []);
      return [];
    }
  }, []);

  const mapRawSkuToOption = useCallback((raw, groupKey) => {
    let resolvedPrice = null;
    for (const key of SKU_PRICE_FIELDS) {
      if (raw && raw[key] !== undefined && raw[key] !== null) {
        const parsed = Number(raw[key]);
        if (Number.isFinite(parsed)) {
          resolvedPrice = parsed;
          break;
        }
      }
    }
    const description =
      raw?.SKUDescription ||
      raw?.Description ||
      raw?.vad_description ||
      raw?.Brand ||
      raw?.Notes ||
      raw?.Narrative ||
      '';
    const skuCode =
      raw?.SKU ??
      raw?.Sku ??
      raw?.sku ??
      raw?.SKUCode ??
      raw?.skuCode ??
      raw?.vad_variant_code ??
      '';
    const uom = raw?.UOM || raw?.UnitOfMeasure || raw?.UnitMeasure || raw?.Measure || raw?.vad_uom || '';
    return {
      KitComponentSkuOptionId: null,
      SKUGroupKey: groupKey,
      SKUKey: raw?.SKUKey ?? raw?.SkuKey ?? null,
      SKU: skuCode,
      Description: description,
      UOM: uom,
      Price: resolvedPrice !== null ? resolvedPrice : 0,
      IsDefault: false,
      IsAllowed: true
    };
  }, []);

  const mapComponentForCopy = useCallback((comp) => {
    if (!comp) return emptyComponent();
    return {
      KitComponentId: null,
      ComponentOEMNumber: comp.ComponentOEMNumber || '',
      Quantity:
        comp.Quantity != null && !Number.isNaN(Number(comp.Quantity))
          ? Number(comp.Quantity)
          : 1,
      OEMNote: comp.OEMNote || '',
      ItemTypeKey: comp.ItemTypeKey ?? null,
      FitmentLocationKey: comp.FitmentLocationKey ?? null,
      KitItemTypeId: comp.KitItemTypeId ?? null,
      KitFitmentLocationId: comp.KitFitmentLocationId ?? null,
      DisplayOrder: comp.DisplayOrder ?? null,
      SKUGroupKey: comp.SKUGroupKey ?? null,
      SKUGroup: normalizeSkuGroupLabel(comp.SKUGroup || comp.SKUGroupName || ''),
      SKUKey: comp.SKUKey ?? null,
      SKU: comp.SKU || '',
      OEMKey: comp.OEMKey ?? null,
      SkuOptions: Array.isArray(comp.SkuOptions)
        ? comp.SkuOptions.map((opt) => ({
            ...opt,
            KitComponentSkuOptionId: null,
            KitComponentId: null,
            SKUGroupKey: opt.SKUGroupKey ?? opt.SkuGroupKey ?? comp.SKUGroupKey ?? null,
            SKUKey: opt.SKUKey ?? opt.SkuKey ?? null,
            SKU: opt.SKU || opt.SKUCode || '',
            SKUCode: opt.SKUCode || opt.SKU || '',
            Description: opt.Description || '',
            UOM: opt.UOM || '',
            Price:
              opt.Price != null && !Number.isNaN(Number(opt.Price))
                ? Number(opt.Price)
                : null,
            IsDefault: opt.IsDefault === true || opt.IsDefault === 1,
            IsAllowed: opt.IsAllowed !== false
          }))
        : []
    };
  }, []);

  const buildSkuOptions = useCallback(
    (rawList, groupKey, preferredSkuKey, preferredSkuNumber) => {
      if (!Array.isArray(rawList) || rawList.length === 0) return { options: [], defaultOption: null };
      const options = rawList.map((raw) => mapRawSkuToOption(raw, groupKey));
      const normalizedPreferredSku = (preferredSkuNumber || '').trim().toLowerCase();

      const findIndexBy = (predicate) => options.findIndex(predicate);

      let defaultIndex = -1;
      if (preferredSkuKey !== null && preferredSkuKey !== undefined) {
        defaultIndex = findIndexBy((opt) => opt.SKUKey === preferredSkuKey);
      }
      if (defaultIndex === -1 && normalizedPreferredSku) {
        defaultIndex = findIndexBy((opt) => (opt.SKU || '').trim().toLowerCase() === normalizedPreferredSku);
      }
      if (defaultIndex === -1) {
        const priced = options
          .map((opt, idx) => ({ idx, price: Number(opt.Price) }))
          .filter(({ price }) => Number.isFinite(price) && price > 0)
          .sort((a, b) => a.price - b.price);
        if (priced.length > 0) {
          defaultIndex = priced[0].idx;
        }
      }
      if (defaultIndex === -1 && options.length > 0) {
        const alphabetical = options
          .map((opt, idx) => ({
            idx,
            label: (opt.SKU || '').toString().trim().toLowerCase()
          }))
          .sort((a, b) => {
            if (a.label && b.label) return a.label.localeCompare(b.label);
            if (a.label) return -1;
            if (b.label) return 1;
            return 0;
          });
        if (alphabetical.length > 0) {
          defaultIndex = alphabetical[0].idx;
        }
      }
      if (defaultIndex === -1 && options.length > 0) {
        defaultIndex = 0;
      }

      const optionsWithFlags = options.map((opt, idx) => ({
        ...opt,
        IsDefault: idx === defaultIndex,
        IsAllowed: true
      }));

      const defaultOption = defaultIndex >= 0 ? optionsWithFlags[defaultIndex] : null;
      return { options: optionsWithFlags, defaultOption };
    },
    [mapRawSkuToOption]
  );

  const invalidateSkuGroupCache = useCallback((groupKey) => {
    if (!groupKey) return;
    skuGroupOptionsCacheRef.current.delete(groupKey);
  }, []);

  const applySkuGroupOptions = useCallback(
    async (index, groupKey, fallbackGroupName = '', preferredSkuKey = null, preferredSkuNumber = null) => {
      if (index === null || index === undefined || index < 0) return;
      if (!groupKey) return;
      const targetComponent = components[index];
      const [rawOptions, resolvedName] = await Promise.all([
        fetchSkuOptionsForGroup(groupKey),
        fallbackGroupName ? Promise.resolve(fallbackGroupName) : fetchSkuGroupName(groupKey)
      ]);
      const { options, defaultOption } = buildSkuOptions(
        rawOptions,
        groupKey,
        preferredSkuKey,
        preferredSkuNumber
      );
      setComponents((prev) =>
        prev.map((comp, idx) => {
          if (idx !== index) return comp;
          const next = {
            ...comp,
            SKUGroupKey: groupKey,
            SKUGroup: normalizeSkuGroupLabel(resolvedName ?? fallbackGroupName ?? comp.SKUGroup ?? ''),
            SkuOptions: options
          };
          if (defaultOption) {
            next.SKUKey = defaultOption.SKUKey ?? null;
            next.SKU = defaultOption.SKU ?? '';
          } else if (options.length === 0) {
            next.SKUKey = null;
            next.SKU = '';
          }
          return next;
        })
      );
      invalidateSkuHydrationForIndex(targetComponent, index);
    },
    [buildSkuOptions, components, fetchSkuGroupName, fetchSkuOptionsForGroup, invalidateSkuHydrationForIndex, setComponents]
  );

  const hydrateSkuOptionsFromOrderwise = useCallback(
    async (componentIndex) => {
      const component = components[componentIndex];
      if (!component) return;
      const options = Array.isArray(component.SkuOptions) ? component.SkuOptions : [];
      const cacheKey = getComponentCacheKey(component, componentIndex);
      if (!options.length) {
        skuOptionsHydrationCacheRef.current.delete(cacheKey);
        return;
      }

      const skuCodes = Array.from(
        new Set(
          options
            .map((opt) => (opt.SKU || opt.SKUCode || opt.sku || '').toString().trim())
            .filter(Boolean)
        )
      );
      if (skuCodes.length === 0) {
        skuOptionsHydrationCacheRef.current.delete(cacheKey);
        return;
      }

      const fingerprint = `${component.SKUGroupKey || component.SKUGroup || ''}|${skuCodes.join('|')}`;
      if (skuOptionsHydrationCacheRef.current.get(cacheKey) === fingerprint) {
        return;
      }

      setSkuOptionsLoading(true);
      setSkuOptionsError('');

      try {
        const token = await fetchOrderwiseToken();
        if (!token) {
          throw new Error('OrderWise token not available.');
        }

        const responses = await Promise.all(
          skuCodes.map(async (skuCode) => {
            try {
              const response = await postOrderwise(
                '/fetch-sku-options',
                [{ name: '@SKU', value: skuCode }],
                token,
                {
                  headers: { 'Content-Type': 'application/json' },
                  timeout: 300000
                }
              );
              const payload = Array.isArray(response) && response.length > 0
                ? response[0]
                : Array.isArray(response?.rows) && response.rows.length > 0
                  ? response.rows[0]
                  : null;
              return payload ? { sku: skuCode, payload } : null;
            } catch (err) {
              console.warn('OrderWise SKU option fetch failed for', skuCode, err);
              return null;
            }
          })
        );

        const detailMap = new Map();
        responses.forEach((entry) => {
          if (!entry) return;
          const { sku, payload } = entry;
          const normalizedSku = (payload.SKU || payload.Sku || payload.sku || payload.SKUCode || payload.sku_code || sku || '')
            .toString()
            .trim()
            .toLowerCase();
          if (!normalizedSku) return;

          let resolvedPrice = null;
          for (const key of SKU_PRICE_FIELDS) {
            if (payload[key] !== undefined && payload[key] !== null) {
              const parsed = Number(payload[key]);
              if (Number.isFinite(parsed)) {
                resolvedPrice = parsed;
                break;
              }
            }
          }

          detailMap.set(normalizedSku, {
            description:
              payload.SKUDescription ||
              payload.Description ||
              payload.description ||
              payload.vad_description ||
              payload.Brand ||
              '',
            uom:
              payload.UOM ||
              payload.UnitOfMeasure ||
              payload.UnitMeasure ||
              payload.Unit ||
              payload.uom ||
              payload.vad_uom ||
              '',
            price: resolvedPrice
          });
        });

        if (detailMap.size === 0) {
          setSkuOptionsError('No OrderWise detail returned for the selected SKUs.');
        }

        setComponents((prev) =>
          prev.map((comp, idx) => {
            if (idx !== componentIndex) return comp;
            const nextOptions = (comp.SkuOptions || []).map((opt) => {
              const key = (opt.SKU || opt.SKUCode || opt.sku || '')
                .toString()
                .trim()
                .toLowerCase();
              const detail = detailMap.get(key);
              if (!detail) return opt;
              return {
                ...opt,
                Description: detail.description || opt.Description,
                UOM: detail.uom || opt.UOM,
                Price:
                  detail.price !== null && detail.price !== undefined
                    ? detail.price
                    : opt.Price
              };
            });
            return { ...comp, SkuOptions: nextOptions };
          })
        );

        skuOptionsHydrationCacheRef.current.set(cacheKey, fingerprint);
      } catch (err) {
        console.error('Failed to hydrate SKU options from OrderWise', err);
        setSkuOptionsError(err?.response?.data?.error || err?.message || 'Failed to load OrderWise data.');
      } finally {
        setSkuOptionsLoading(false);
      }
    },
    [components, fetchOrderwiseToken, getComponentCacheKey, postOrderwise, setComponents, setSkuOptionsError, setSkuOptionsLoading]
  );

  const ensureBaseFitmentLocations = useCallback(async (itemTypeKey) => {
    if (itemTypeKey === null || itemTypeKey === undefined) return;
    const keyNum = Number(itemTypeKey);
    if (Number.isNaN(keyNum)) return;
    if (baseFitmentLocationsByItemTypeRef.current.has(keyNum)) return;
    try {
      const locs = await api.listFitmentLocationsForItemType(keyNum);
      baseFitmentLocationsByItemTypeRef.current.set(keyNum, Array.isArray(locs) ? locs : []);
      setBaseLocationVersion((version) => version + 1);
    } catch (err) {
      console.warn('Failed to fetch fitment locations', err);
    }
  }, []);

  const allBaseFitmentLocations = useMemo(() => {
    const unique = new Map();
    baseFitmentLocationsByItemTypeRef.current.forEach((list) => {
      (list || []).forEach((loc) => {
        if (loc?.FitmentLocationKey === null || loc?.FitmentLocationKey === undefined) return;
        if (!unique.has(loc.FitmentLocationKey)) {
          unique.set(loc.FitmentLocationKey, loc);
        }
      });
    });
    return Array.from(unique.values()).sort((a, b) =>
      String(a?.FitmentLocation || '').localeCompare(String(b?.FitmentLocation || ''), undefined, { sensitivity: 'base' })
    );
  }, [baseLocationVersion]);

  useEffect(() => {
    if (!skuOptionsModal.open) return;
    if (skuOptionsModal.componentIndex === null || skuOptionsModal.componentIndex < 0) return;
    hydrateSkuOptionsFromOrderwise(skuOptionsModal.componentIndex);
  }, [skuOptionsModal, hydrateSkuOptionsFromOrderwise]);

  useEffect(() => {
    if (!open) {
      skuOptionsHydrationCacheRef.current.clear();
    }
  }, [open]);

  useEffect(() => {
    skuOptionsHydrationCacheRef.current.clear();
  }, [kitId]);

  const itemTypeOptions = useMemo(() => {
    const core = (coreItemTypes || []).map((it) => ({
      key: `core:${it.ItemTypeKey}`,
      label: it.ItemType,
      source: 'core',
      baseKey: it.ItemTypeKey
    }));
    const kitTypes = (kitItemTypes || []).map((it) => ({
      key: `kit:${it.KitItemTypeId}`,
      label: it.Name,
      source: 'kit',
      kitId: it.KitItemTypeId
    }));
    return [...core, ...kitTypes].sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
    );
  }, [coreItemTypes, kitItemTypes]);

  const fitmentLocationOptions = useMemo(() => {
    const base = allBaseFitmentLocations.map((loc) => ({
      key: `core:${loc.FitmentLocationKey}`,
      label: formatFitmentLocationLabel(loc.FitmentLocation),
      source: 'core',
      baseKey: loc.FitmentLocationKey
    }));
    const kitLocs = (kitFitmentLocations || []).map((loc) => ({
      key: `kit:${loc.KitFitmentLocationId}`,
      label: loc.Name,
      source: 'kit',
      kitId: loc.KitFitmentLocationId
    }));
    return [...base, ...kitLocs].sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
    );
  }, [allBaseFitmentLocations, kitFitmentLocations]);

  // Return fitment-location options scoped to a single ItemTypeKey.
  // Falls back to the full merged list when the key is null/undefined or
  // no locations have been loaded for it yet.
  const getFitmentLocationOptionsForItemType = useCallback((itemTypeKey) => {
    const keyNum = itemTypeKey != null ? Number(itemTypeKey) : NaN;
    const kitLocs = (kitFitmentLocations || []).map((loc) => ({
      key: `kit:${loc.KitFitmentLocationId}`,
      label: loc.Name,
      source: 'kit',
      kitId: loc.KitFitmentLocationId
    }));
    if (Number.isNaN(keyNum) || !baseFitmentLocationsByItemTypeRef.current.has(keyNum)) {
      // Fallback: return the full merged list (same as fitmentLocationOptions)
      return fitmentLocationOptions;
    }
    const typeSpecificLocs = baseFitmentLocationsByItemTypeRef.current.get(keyNum) || [];
    const base = typeSpecificLocs
      .filter(loc => loc?.FitmentLocationKey != null)
      .map(loc => ({
        key: `core:${loc.FitmentLocationKey}`,
        label: formatFitmentLocationLabel(loc.FitmentLocation),
        source: 'core',
        baseKey: loc.FitmentLocationKey
      }));
    return [...base, ...kitLocs].sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
    );
  }, [fitmentLocationOptions, kitFitmentLocations, baseLocationVersion]);

  const resolveItemTypeLabel = useCallback(() => {
    const candidates = [
      fitmentContext?.ItemType,
      fitmentContext?.itemType,
      fitmentContext?.ItemTypeName,
      fitmentContext?.ItemTypeLabel,
      fitmentContext?.ItemTypeDescription
    ];
    const fromContext = candidates.find(value => value && String(value).trim().length > 0);
    if (fromContext) return String(fromContext).trim();

    if (baseItemTypeKey !== null && baseItemTypeKey !== undefined) {
      const match = itemTypeOptions.find(opt => opt.source === 'core' && opt.baseKey === baseItemTypeKey);
      if (match?.label) return match.label;
    }

    if (baseKitItemTypeId !== null && baseKitItemTypeId !== undefined) {
      const match = itemTypeOptions.find(opt => opt.source === 'kit' && opt.kitId === baseKitItemTypeId);
      if (match?.label) return match.label;
    }

    return '';
  }, [fitmentContext, baseItemTypeKey, baseKitItemTypeId, itemTypeOptions]);

  const resolveFitmentLocationLabel = useCallback(() => {
    const candidates = [
      fitmentContext?.FitmentLocation,
      fitmentContext?.fitmentLocation,
      fitmentContext?.Location,
      fitmentContext?.FitmentLocationName
    ];
    const fromContext = candidates.find(value => value && String(value).trim().length > 0);
    if (fromContext) return String(fromContext).trim();

    if (baseFitmentLocationKey !== null && baseFitmentLocationKey !== undefined) {
      const match = fitmentLocationOptions.find(opt => opt.source === 'core' && opt.baseKey === baseFitmentLocationKey);
      if (match?.label) return match.label;
    }

    if (baseKitFitmentLocationId !== null && baseKitFitmentLocationId !== undefined) {
      const match = fitmentLocationOptions.find(opt => opt.source === 'kit' && opt.kitId === baseKitFitmentLocationId);
      if (match?.label) return match.label;
    }

    return '';
  }, [fitmentContext, baseFitmentLocationKey, baseKitFitmentLocationId, fitmentLocationOptions]);

  const deriveDefaultDescription = useCallback(() => {
    const typeLabel = resolveItemTypeLabel();
    if (typeLabel && typeLabel.trim().length > 0) {
      return `${typeLabel.trim()} Kit`;
    }
    return 'New Kit';
  }, [resolveItemTypeLabel]);

  const deriveDefaultDescriptionRef = useRef(deriveDefaultDescription);
  deriveDefaultDescriptionRef.current = deriveDefaultDescription;

  const getItemTypeOptionKey = (baseKey, kitId) => {
    if (baseKey !== null && baseKey !== undefined) return `core:${baseKey}`;
    if (kitId !== null && kitId !== undefined) return `kit:${kitId}`;
    return '';
  };

  const getFitmentLocationOptionKey = (baseKey, kitId) => {
    if (baseKey !== null && baseKey !== undefined) return `core:${baseKey}`;
    if (kitId !== null && kitId !== undefined) return `kit:${kitId}`;
    return '';
  };

  const parseOptionKey = (value) => {
    if (!value) return { source: null, id: null };
    const [source, rawId] = value.split(':');
    const idNum = rawId === undefined ? null : Number(rawId);
    return {
      source: source || null,
      id: Number.isNaN(idNum) ? null : idNum
    };
  };

  const handleKitItemTypeChange = useCallback((optionKey) => {
    const { source, id } = parseOptionKey(optionKey);
    if (!source || id === null) {
      setKit((prev) => ({ ...prev, BaseItemTypeKey: null, BaseKitItemTypeId: null }));
      return;
    }
    if (source === 'core') {
      ensureBaseFitmentLocations(id);
      setKit((prev) => ({ ...prev, BaseItemTypeKey: id, BaseKitItemTypeId: null }));
    } else if (source === 'kit') {
      setKit((prev) => ({ ...prev, BaseItemTypeKey: null, BaseKitItemTypeId: id }));
    }
  }, [ensureBaseFitmentLocations]);

  const handleKitFitmentLocationChange = useCallback((optionKey) => {
    const { source, id } = parseOptionKey(optionKey);
    if (!source || id === null) {
      setKit((prev) => ({ ...prev, BaseFitmentLocationKey: null, BaseKitFitmentLocationId: null }));
      return;
    }
    if (source === 'core') {
      setKit((prev) => ({ ...prev, BaseFitmentLocationKey: id, BaseKitFitmentLocationId: null }));
    } else if (source === 'kit') {
      setKit((prev) => ({ ...prev, BaseFitmentLocationKey: null, BaseKitFitmentLocationId: id }));
    }
  }, []);

  const handleComponentItemTypeChange = useCallback((index, optionKey) => {
    const { source, id } = parseOptionKey(optionKey);
    setComponents((prev) => prev.map((comp, idx) => {
      if (idx !== index) return comp;
      if (!source || id === null) {
        return { ...comp, ItemTypeKey: null, KitItemTypeId: null };
      }
      if (source === 'core') {
        ensureBaseFitmentLocations(id);
        return { ...comp, ItemTypeKey: id, KitItemTypeId: null };
      }
      if (source === 'kit') {
        return { ...comp, ItemTypeKey: null, KitItemTypeId: id };
      }
      return comp;
    }));
  }, [ensureBaseFitmentLocations]);

  const handleComponentFitmentLocationChange = useCallback((index, optionKey) => {
    const { source, id } = parseOptionKey(optionKey);
    setComponents((prev) => prev.map((comp, idx) => {
      if (idx !== index) return comp;
      if (!source || id === null) {
        return { ...comp, FitmentLocationKey: null, KitFitmentLocationId: null };
      }
      if (source === 'core') {
        return { ...comp, FitmentLocationKey: id, KitFitmentLocationId: null };
      }
      if (source === 'kit') {
        return { ...comp, FitmentLocationKey: null, KitFitmentLocationId: id };
      }
      return comp;
    }));
  }, []);

  const displaySkuGroup = useCallback((comp) => {
    if (!comp) return '';
    if (comp.SKUGroup && comp.SKUGroupKey) return `${comp.SKUGroup} (#${comp.SKUGroupKey})`;
    if (comp.SKUGroup) return comp.SKUGroup;
    if (comp.SKUGroupKey) return `#${comp.SKUGroupKey}`;
    return '';
  }, []);

  const displaySku = useCallback((comp) => {
    if (!comp) return '';
    if (comp.SKU && comp.SKUKey) return `${comp.SKU} (#${comp.SKUKey})`;
    if (comp.SKU) return comp.SKU;
    if (comp.SKUKey) return `#${comp.SKUKey}`;
    return '';
  }, []);

  const openManageOEMForComponent = useCallback((index) => {
    const comp = components[index];
    setActiveComponentIndex(index);
    setManageOEMState({
      open: true,
      componentIndex: index,
      initialNumber: comp?.ComponentOEMNumber || '',
      initialKey: comp?.OEMKey ?? null
    });
  }, [components]);

  const openManageSKUForComponent = useCallback((index) => {
    const comp = components[index];
    setActiveComponentIndex(index);
    setManageSKUState({
      open: true,
      componentIndex: index,
      initialGroup: comp?.SKUGroup || '',
      initialSku: comp?.SKU || ''
    });
  }, [components]);

  const openManageOEMFromHeader = useCallback(() => {
    if (!kitId) return;
    setManageOEMState({
      open: true,
      componentIndex: null,
      initialNumber: kitId,
      initialKey: null
    });
  }, [kitId]);

  const closeManageOEM = useCallback(() => {
    setManageOEMState({ open: false, componentIndex: null, initialNumber: '', initialKey: null });
  }, []);

  const closeManageSKU = useCallback(() => {
    setManageSKUState({ open: false, componentIndex: null, initialGroup: '', initialSku: '' });
  }, []);

  // ── Kit-level inline SKU→SKUGroup lookup callbacks ──
  const debouncedKitSkuGroupSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) { setKitSkuGroupResults([]); return; }
      setKitSkuGroupBusy(true);
      try {
        const { data } = await axios.get('/api/sku-groups/search', { params: { query, pageSize: 50 } });
        setKitSkuGroupResults(data || []);
      } catch { setKitSkuGroupResults([]); }
      finally { setKitSkuGroupBusy(false); }
    }, 300),
    []
  );
  const debouncedKitSkuLookup = useCallback(
    debounce(async (query) => {
      const q = (query || '').trim();
      if (!q || q.length < 2) { setKitSkuLookupGroups([]); setKitSkuLookupKey(null); setKitSkuLookupDone(false); return; }
      setKitSkuLookupBusy(true);
      try {
        const { data } = await axios.get(`/api/sku-by-number/${encodeURIComponent(q)}/groups`);
        setKitSkuLookupKey(data?.skuKey || null);
        setKitSkuLookupGroups(data?.groups || []);
        setKitSkuLookupDone(true);
      } catch { setKitSkuLookupKey(null); setKitSkuLookupGroups([]); setKitSkuLookupDone(false); }
      finally { setKitSkuLookupBusy(false); }
    }, 400),
    []
  );
  useEffect(() => { debouncedKitSkuGroupSearch(kitSkuGroupSearch); }, [kitSkuGroupSearch, debouncedKitSkuGroupSearch]);
  useEffect(() => { debouncedKitSkuLookup(kitSkuGroupSearch); }, [kitSkuGroupSearch, debouncedKitSkuLookup]);
  useEffect(() => {
    if (!showKitSkuGroupDropdown) {
      setKitCreateGroupMode(false); setKitNewGroupName(''); setKitCreateGroupError(''); setKitMergeWarning(null);
    }
  }, [showKitSkuGroupDropdown]);

  const loadKitSkuGroupName = useCallback(async (key) => {
    if (!key) return;
    if (kitSkuGroupNameCacheRef2.current[key]) { setKitSkuGroupSearch(kitSkuGroupNameCacheRef2.current[key]); return; }
    try {
      const res = await axios.get(`/api/sku-groups/${key}`);
      const name = res.data?.SKUGroup || '';
      setKitSkuGroupSearch(name);
      kitSkuGroupNameCacheRef2.current[key] = name;
    } catch { /* ignore */ }
  }, []);

  const handleKitCreateGroupForSku = useCallback(async (groupName) => {
    if (!groupName?.trim()) return;
    setKitCreateGroupBusy(true); setKitCreateGroupError(''); setKitMergeWarning(null);
    try {
      const { data: searchHits } = await axios.get('/api/sku-groups/search', { params: { query: groupName.trim(), pageSize: 5 } });
      const exactMatch = (searchHits || []).find(g => (g.SKUGroup || '').trim().toLowerCase() === groupName.trim().toLowerCase());
      if (exactMatch) {
        setKitMergeWarning({ existingKey: exactMatch.SKUGroupKey, existingName: exactMatch.SKUGroup });
        setKitCreateGroupBusy(false);
        return;
      }
      const payload = { sku: kitSkuLookupKey ? kitSkuGroupSearch.trim() : null, skuGroupName: groupName.trim(), createGroup: true, createSku: false };
      const { data: result } = await axios.post('/api/kits/import-resolve-sku', payload);
      if (result?.skuGroupKey) {
        setKit(prev => ({ ...prev, SKUGroupKey: result.skuGroupKey, SKUGroup: normalizeSkuGroupLabel(groupName.trim()) }));
        setKitSkuGroupSearch(groupName.trim());
        kitSkuGroupNameCacheRef2.current[result.skuGroupKey] = groupName.trim();
        setShowKitSkuGroupDropdown(false); setKitCreateGroupMode(false); setKitNewGroupName('');
      }
    } catch (err) { setKitCreateGroupError(err?.response?.data?.error || 'Failed to create group'); }
    finally { setKitCreateGroupBusy(false); }
  }, [kitSkuGroupSearch, kitSkuLookupKey]);

  const handleKitUseExistingGroup = useCallback(async (group) => {
    if (!group?.SKUGroupKey) return;
    try {
      if (kitSkuLookupKey) {
        await axios.post('/api/kits/import-resolve-sku', { sku: kitSkuGroupSearch.trim(), addToGroupKey: group.SKUGroupKey, createSku: false });
      }
      setKit(prev => ({ ...prev, SKUGroupKey: group.SKUGroupKey, SKUGroup: normalizeSkuGroupLabel(group.SKUGroup || '') }));
      setKitSkuGroupSearch(group.SKUGroup || '');
      kitSkuGroupNameCacheRef2.current[group.SKUGroupKey] = group.SKUGroup || '';
      setShowKitSkuGroupDropdown(false);
    } catch {
      setKit(prev => ({ ...prev, SKUGroupKey: group.SKUGroupKey, SKUGroup: normalizeSkuGroupLabel(group.SKUGroup || '') }));
      setKitSkuGroupSearch(group.SKUGroup || '');
      setShowKitSkuGroupDropdown(false);
    }
  }, [kitSkuGroupSearch, kitSkuLookupKey]);

  const handleKitMergeConfirm = useCallback(async () => {
    if (!kitMergeWarning?.existingKey) return;
    setKitCreateGroupBusy(true);
    try {
      if (kitSkuLookupKey) {
        await axios.post('/api/kits/import-resolve-sku', { sku: kitSkuGroupSearch.trim(), addToGroupKey: kitMergeWarning.existingKey, createSku: false });
      }
      setKit(prev => ({ ...prev, SKUGroupKey: kitMergeWarning.existingKey, SKUGroup: normalizeSkuGroupLabel(kitMergeWarning.existingName) }));
      setKitSkuGroupSearch(kitMergeWarning.existingName);
      kitSkuGroupNameCacheRef2.current[kitMergeWarning.existingKey] = kitMergeWarning.existingName;
      setShowKitSkuGroupDropdown(false); setKitCreateGroupMode(false); setKitMergeWarning(null); setKitNewGroupName('');
    } catch (err) { setKitCreateGroupError(err?.response?.data?.error || 'Failed to merge'); }
    finally { setKitCreateGroupBusy(false); }
  }, [kitMergeWarning, kitSkuGroupSearch, kitSkuLookupKey]);

  const applyManageOEMSelection = useCallback((selection) => {
    if (manageOEMState.componentIndex === null || manageOEMState.componentIndex < 0) {
      closeManageOEM();
      return;
    }
    const targetIndex = manageOEMState.componentIndex;
    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== targetIndex) return comp;
      const nextGroupLabel = selection?.SKUGroup ?? comp.SKUGroup;
      return {
        ...comp,
        ComponentOEMNumber: selection?.OEMNumber ?? comp.ComponentOEMNumber,
        OEMKey: selection?.OEMKey ?? comp.OEMKey,
        SKUGroupKey: selection?.SKUGroupKey ?? comp.SKUGroupKey,
        SKUGroup: normalizeSkuGroupLabel(nextGroupLabel),
        OEMNote:
          selection?.OEMNote && (!comp.OEMNote || !comp.OEMNote.trim())
            ? selection.OEMNote
            : comp.OEMNote,
        SKUKey: selection?.SKUKey ?? comp.SKUKey,
        SKU: selection?.SKU ?? comp.SKU
      };
    }));
    if (selection?.SKUGroupKey) {
      invalidateSkuGroupCache(selection.SKUGroupKey);
      applySkuGroupOptions(
        targetIndex,
        selection.SKUGroupKey,
        selection.SKUGroup ?? '',
        selection.SKUKey ?? null,
        selection.SKU ?? null
      ).catch((err) => console.warn('Failed to apply SKU options from OEM modal', err));
    }
    closeManageOEM();
  }, [manageOEMState.componentIndex, closeManageOEM, applySkuGroupOptions, invalidateSkuGroupCache]);

  const applyManageSKUSelection = useCallback((selection) => {
    if (manageSKUState.componentIndex === null || manageSKUState.componentIndex < 0) {
      closeManageSKU();
      return;
    }
    const targetIndex = manageSKUState.componentIndex;
    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== targetIndex) return comp;
      const nextGroupLabel = selection?.SKUGroup ?? comp.SKUGroup;
      return {
        ...comp,
        SKUGroupKey: selection?.SKUGroupKey ?? comp.SKUGroupKey,
        SKUGroup: normalizeSkuGroupLabel(nextGroupLabel),
        SKUKey: selection?.SKUKey ?? comp.SKUKey,
        SKU: selection?.SKU ?? comp.SKU
      };
    }));
    if (selection?.SKUGroupKey) {
      invalidateSkuGroupCache(selection.SKUGroupKey);
      applySkuGroupOptions(
        targetIndex,
        selection.SKUGroupKey,
        selection.SKUGroup ?? '',
        selection.SKUKey ?? null,
        selection.SKU ?? null
      ).catch((err) => console.warn('Failed to apply SKU options from SKU modal', err));
    }
    closeManageSKU();
  }, [manageSKUState.componentIndex, closeManageSKU, applySkuGroupOptions, invalidateSkuGroupCache]);

  const openCopyModal = useCallback(() => {
    setCopyModalState(() => ({
      ...createCopyModalState(),
      open: true
    }));
  }, []);

  const closeCopyModal = useCallback(() => {
    setCopyModalState(() => createCopyModalState());
  }, []);

  const handleCopySearchInput = useCallback((value) => {
    setCopyModalState((prev) => ({ ...prev, searchDraft: value }));
  }, []);

  const applyCopySearch = useCallback(() => {
    setCopyModalState((prev) => {
      const normalized = (prev.searchDraft || '').trim();
      return { ...prev, search: normalized, searchDraft: normalized, page: 1 };
    });
  }, []);

  const resetCopySearch = useCallback(() => {
    setCopyModalState((prev) => ({ ...prev, search: '', searchDraft: '', page: 1 }));
  }, []);

  const handleCopyPageChange = useCallback((direction) => {
    setCopyModalState((prev) => {
      const totalRows = prev.total || 0;
      if (totalRows === 0) return prev;
      const pageSize = prev.pageSize || COPY_MODAL_PAGE_SIZE;
      const maxPage = Math.max(1, Math.ceil(totalRows / pageSize));
      const nextPage = direction === 'next' ? prev.page + 1 : prev.page - 1;
      if (nextPage < 1 || nextPage > maxPage) return prev;
      return { ...prev, page: nextPage };
    });
  }, []);

  const fetchCopyCandidates = useCallback(async (page, term) => {
    const safePage = Math.max(1, page || 1);
    const searchTerm = (term || '').trim();
    setCopyModalState((prev) => ({ ...prev, loading: true, error: '', page: safePage }));
    try {
      const data = await api.listKits({
        search: searchTerm || undefined,
        limit: copyPageSize,
        offset: (safePage - 1) * copyPageSize,
        includeInactive: false
      });
      const totalRows = data?.total || 0;
      const maxPage = totalRows > 0 ? Math.max(1, Math.ceil(totalRows / copyPageSize)) : 1;
      const normalizedPage = Math.min(safePage, maxPage);
      setCopyModalState((prev) => ({
        ...prev,
        loading: false,
        total: totalRows,
        results: normalizedPage === safePage ? (Array.isArray(data?.rows) ? data.rows : []) : (prev.results || []),
        page: normalizedPage
      }));
    } catch (err) {
      console.error('Failed to load kits', err);
      setCopyModalState((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.error || err?.message || 'Failed to load kits.'
      }));
    }
  }, [copyPageSize]);

  const handleCopyKit = useCallback(async (sourceKitId) => {
    if (!sourceKitId) return;
    if (kitId && !window.confirm('Copying will discard the currently loaded kit. Continue?')) {
      return;
    }
    setCopyModalState((prev) => ({ ...prev, copyingId: sourceKitId, error: '' }));
    try {
      const detail = await api.fetchKit(sourceKitId);
      const sourceKit = detail?.kit || {};
      const rawComponents = Array.isArray(detail?.components) ? [...detail.components] : [];
      rawComponents.sort((a, b) => (a?.DisplayOrder ?? 0) - (b?.DisplayOrder ?? 0));
      const mappedComponents = rawComponents.length ? rawComponents.map(mapComponentForCopy) : [emptyComponent()];

      setKit({
        Description: sourceKit.Description || '',
        BaseItemTypeKey: sourceKit.BaseItemTypeKey ?? null,
        BaseKitItemTypeId: sourceKit.BaseKitItemTypeId ?? null,
        BaseFitmentLocationKey: sourceKit.BaseFitmentLocationKey ?? null,
        BaseKitFitmentLocationId: sourceKit.BaseKitFitmentLocationId ?? null,
        FitmentInfo: sourceKit.FitmentInfo || ''
      });
      setComponents(mappedComponents);
      setKitId(null);
      setHistory([]);
      setHistorySelection(null);
      setReuseCandidates([]);
      setStockSummary(null);
      setSuppressInitialKitId(true);
      autoHydratedOEMsRef.current = new Set();
      skuGroupOptionsCacheRef.current = new Map();
      skuGroupNameCacheRef.current = new Map();
      hasAutoLoadedSuggestionsRef.current = false;
      setInfo(`Copied kit ${sourceKitId} into a new draft. Save to create a new kit ID.`);
      closeCopyModal();
    } catch (err) {
      console.error('Failed to copy kit', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to copy kit.';
      setCopyModalState((prev) => ({ ...prev, copyingId: null, error: message }));
      setError(message);
    } finally {
      setCopyModalState((prev) => ({ ...prev, copyingId: null }));
    }
  }, [kitId, mapComponentForCopy, closeCopyModal]);

  useEffect(() => {
    if (!copyModalState.open) return;
    fetchCopyCandidates(copyModalState.page, (copyModalState.search || '').trim());
  }, [copyModalState.open, copyModalState.page, copyModalState.search, fetchCopyCandidates]);

  const lookupOEMDetails = useCallback(async (rawNumber) => {
    const key = (rawNumber || '').trim();
    if (!key) return null;
    if (oemLookupCacheRef.current.has(key)) {
      return oemLookupCacheRef.current.get(key);
    }

    let resolved = null;
    try {
      const { data } = await axios.get('/api/oems/search', {
        params: { query: key, pageSize: 10 }
      });
      if (Array.isArray(data) && data.length > 0) {
        const lowerKey = key.toLowerCase();
        const exact = data.find(row => String(row?.OEMNumber || '').trim().toLowerCase() === lowerKey);
        const match = exact || data[0];
        if (match) {
          let detail = null;
          if (match?.OEMKey) {
            try {
              const response = await axios.get(`/api/oems/${match.OEMKey}`);
              detail = response?.data || null;
            } catch (detailErr) {
              console.warn('Failed to load OEM detail', detailErr);
            }
          }
          const source = detail || match;
          resolved = {
            OEMKey: source?.OEMKey ?? match?.OEMKey ?? null,
            OEMNumber: source?.OEMNumber ?? match?.OEMNumber ?? key,
            OEMNote: source?.OEMNote || match?.OEMNote || match?.Note || '',
            SKUGroupKey:
              source?.SKUGroupKey ??
              source?.PrimarySKUGroupKey ??
              match?.SKUGroupKey ??
              match?.SKUGroup?.SKUGroupKey ??
              null,
            SKUGroup:
              source?.SKUGroup ??
              source?.SKUGroupName ??
              source?.SKUGroupCode ??
              match?.SKUGroup ??
              match?.SKUGroupName ??
              match?.SKUGroupCode ??
              '',
            SKUKey: source?.SKUKey ?? source?.PrimarySKUKey ?? match?.SKUKey ?? null,
            SKU: source?.SKU ?? source?.PrimarySKU ?? match?.SKU ?? match?.SKUCode ?? ''
          };
        }
      }
    } catch (err) {
      console.warn('OEM lookup failed', err);
    }

    oemLookupCacheRef.current.set(key, resolved);
    return resolved;
  }, []);

  const handleComponentOEMBlur = useCallback(async (index, rawValue) => {
    if (!canModify) return;
    const trimmed = (rawValue || '').trim();
    if (!trimmed) return;
    const details = await lookupOEMDetails(trimmed);
    if (!details) return;

    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== index) return comp;
      const next = { ...comp, ComponentOEMNumber: trimmed };
      if (details.OEMKey) next.OEMKey = details.OEMKey;
      if (details.OEMNote && (!comp.OEMNote || !comp.OEMNote.trim())) {
        next.OEMNote = details.OEMNote;
      }
      if (details.SKUGroupKey) {
        next.SKUGroupKey = details.SKUGroupKey;
      }
      if (details.SKUGroup) {
        next.SKUGroup = details.SKUGroup;
      }
      if (details.SKUKey) {
        next.SKUKey = details.SKUKey;
      }
      if (details.SKU) {
        next.SKU = details.SKU;
      }
      return next;
    }));
    if (details.SKUGroupKey) {
      try {
        await applySkuGroupOptions(
          index,
          details.SKUGroupKey,
          details.SKUGroup,
          details.SKUKey ?? null,
          details.SKU ?? null
        );
      } catch (err) {
        console.warn('Failed to populate SKU options from OEM lookup', err);
      }
    }
  }, [lookupOEMDetails, canModify, applySkuGroupOptions]);


  const openSkuOptionsModal = useCallback((index) => {
    setSkuOptionsError('');
    setSkuOptionsLoading(false);
    setSkuOptionsModal({ open: true, componentIndex: index });
  }, []);

  const closeSkuOptionsModal = useCallback(() => {
    setSkuOptionsModal({ open: false, componentIndex: null });
    setSkuOptionsError('');
    setSkuOptionsLoading(false);
  }, []);

  const handleSetSkuOptionDefault = useCallback((componentIndex, optionIndex) => {
    if (componentIndex == null || optionIndex == null) return;
    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== componentIndex) return comp;
      if (!Array.isArray(comp.SkuOptions) || comp.SkuOptions.length === 0) {
        return comp;
      }
      const updatedOptions = comp.SkuOptions.map((opt, optIdx) => {
        const isDefault = optIdx === optionIndex;
        return {
          ...opt,
          IsDefault: isDefault,
          IsAllowed: isDefault ? true : opt.IsAllowed !== false
        };
      });
      const defaultOption = updatedOptions[optionIndex];
      const nextSkuKey = defaultOption && defaultOption.SKUKey != null
        ? defaultOption.SKUKey
        : (comp.SKUKey ?? null);
      const optionSkuValueRaw = defaultOption && (defaultOption.SKU || defaultOption.SKUCode || defaultOption.sku || defaultOption.skuCode);
      const optionSkuValue = optionSkuValueRaw != null ? String(optionSkuValueRaw) : '';
      const nextSku = optionSkuValue.length > 0
        ? optionSkuValue
        : (comp.SKU || '');
      return {
        ...comp,
        SkuOptions: updatedOptions,
        SKUKey: nextSkuKey,
        SKU: nextSku
      };
    }));
  }, [setComponents]);

  const handleToggleSkuOptionAllowed = useCallback((componentIndex, optionIndex, allowed) => {
    if (componentIndex == null || optionIndex == null) return;
    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== componentIndex) return comp;
      if (!Array.isArray(comp.SkuOptions) || comp.SkuOptions.length === 0) {
        return comp;
      }
      const updatedOptions = comp.SkuOptions.map((opt, optIdx) => {
        if (optIdx !== optionIndex) {
          return opt;
        }
        if (opt.IsDefault) {
          return { ...opt, IsAllowed: true };
        }
        return { ...opt, IsAllowed: !!allowed };
      });
      return {
        ...comp,
        SkuOptions: updatedOptions
      };
    }));
  }, [setComponents]);

  const refreshReuseSuggestions = useCallback(async ({ silent = false } = {}) => {
    try {
      const suggestionOptions = {
        ...tolerance,
        matchModes: ['oemQuantity', 'skuQuantity']
      };
      const suggestions = await api.fetchReuseSuggestions(components, suggestionOptions);
      const filtered = Array.isArray(suggestions)
        ? suggestions.filter(candidate => {
            if (!candidate || candidate.kitId == null) return true;
            if (!kitId) return true;
            return String(candidate.kitId) !== String(kitId);
          })
        : [];
      setReuseCandidates(filtered);
      if (!silent) {
        setInfo('Reuse suggestions refreshed.');
      }
    } catch (err) {
      console.warn('Reuse suggestion refresh failed', err);
      setError(err?.response?.data?.error || 'Failed to refresh reuse suggestions');
    }
  }, [components, tolerance, kitId]);

  const handleHistoryApply = useCallback(
    async (entry, direction) => {
      if (!kitId || !entry) return;
      const auditId = entry.KitAuditLogId;
      const normalizedDirection = direction === 'after' ? 'after' : 'before';
      setHistoryModalError('');
      setHistoryApplyBusyId(auditId);
      try {
        await api.applyKitHistory(kitId, auditId, normalizedDirection);
        const detail = await api.fetchKit(kitId);
        hydrateKitDetail(detail);
        await refreshHistory(kitId, auditId);
        setInfo(
          normalizedDirection === 'before'
            ? 'Kit restored to the chosen snapshot.'
            : 'Kit advanced to the chosen snapshot.'
        );
        refreshReuseSuggestions({ silent: true }).catch(() => {});
      } catch (err) {
        console.error('Failed to apply kit history entry', err);
        setHistoryModalError(err?.response?.data?.error || 'Failed to apply history entry.');
      } finally {
        setHistoryApplyBusyId(null);
      }
    },
    [kitId, hydrateKitDetail, refreshHistory, refreshReuseSuggestions, setHistoryApplyBusyId, setHistoryModalError, setInfo]
  );

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [kitTypes, kitLocs, baseTypes] = await Promise.all([
          api.listKitItemTypes(),
          api.listKitFitmentLocations(),
          api.listCoreItemTypes()
        ]);
        setKitItemTypes(kitTypes);
        setKitFitmentLocations(kitLocs);
        setCoreItemTypes(baseTypes);
        if (fitmentContext?.ItemTypeKey) {
          ensureBaseFitmentLocations(fitmentContext.ItemTypeKey);
        }
      } catch (err) {
        console.error('Failed to load kit lookups', err);
        setError('Failed to load kit lookups.');
      }
    })();
  }, [open, fitmentContext, ensureBaseFitmentLocations]);

  useEffect(() => {
    setLocalFitmentKey(fitmentContext?.FitmentKey ?? null);
  }, [fitmentContext?.FitmentKey]);

  useEffect(() => {
    if (!open) return;
    if (kit?.BaseItemTypeKey) ensureBaseFitmentLocations(kit.BaseItemTypeKey);
    components.forEach((comp) => {
      if (comp?.ItemTypeKey) ensureBaseFitmentLocations(comp.ItemTypeKey);
    });
  }, [open, kit?.BaseItemTypeKey, components, ensureBaseFitmentLocations]);

  useEffect(() => {
    if (!open || !!kitId || !canModify) return;
    const pendingLookups = [];
    components.forEach((comp, index) => {
      if (comp?.SKUGroupKey) return;
      const trimmed = (comp?.ComponentOEMNumber || '').trim();
      if (!trimmed) return;
      const trackerKey = `${index}:${trimmed}`;
      if (autoHydratedOEMsRef.current.has(trackerKey)) return;
      autoHydratedOEMsRef.current.add(trackerKey);
      pendingLookups.push({ index, value: trimmed });
    });
    pendingLookups.forEach(({ index, value }) => {
      handleComponentOEMBlur(index, value);
    });
  }, [open, kitId, canModify, components, handleComponentOEMBlur]);

  useEffect(() => {
    if (open && initialKitId && initialKitId !== kitId && !suppressInitialKitId) {
      setKitId(initialKitId);
    }
  }, [open, initialKitId, kitId, suppressInitialKitId]);

  useEffect(() => {
    if (!open) {
      const resetDescription = deriveDefaultDescriptionRef.current();
      lastDefaultDescriptionRef.current = resetDescription;
      setKitId(initialKitId || null);
      // Base keys are seeded from the *next* fitmentContext when the modal opens;
      // seeding them here from the closing context would block that.
      setKit({
        Description: resetDescription,
        BaseItemTypeKey: null,
        BaseKitItemTypeId: null,
        BaseFitmentLocationKey: null,
        BaseKitFitmentLocationId: null,
        FitmentInfo: ''
      });
      setComponents([emptyComponent()]);
      setProductOEMs([]);
      setProductOEMInput('');
      setRefreshImporterKitId(null);
      setHistory([]);
  setHistorySelection(null);
  setHistoryModalOpen(false);
  setHistoryModalError('');
  setHistoryApplyBusyId(null);
      setReuseCandidates([]);
      setError('');
      setInfo('');
      setStockSummary(null);
      setLocalFitmentKey(fitmentContext?.FitmentKey ?? null);
      baseFitmentLocationsByItemTypeRef.current = new Map();
      setBaseLocationVersion((version) => version + 1);
      setManageOEMState({ open: false, componentIndex: null, initialNumber: '', initialKey: null });
  setManageSKUState({ open: false, componentIndex: null, initialGroup: '', initialSku: '' });
      skuGroupOptionsCacheRef.current = new Map();
      skuGroupNameCacheRef.current = new Map();
      autoHydratedOEMsRef.current = new Set();
      setCopyModalState(() => createCopyModalState());
      setSuppressInitialKitId(false);
      return;
    }

    if (!kitId && initialKitId) {
      return;
    }

    if (!kitId) {
      if (Array.isArray(initialComponents) && initialComponents.length > 0) {
        setComponents(initialComponents.map((seed) => ({ ...emptyComponent(), ...seed })));
      } else {
        const initialComponent = emptyComponent();
        if (fitmentContext?.OEMNumber) initialComponent.ComponentOEMNumber = fitmentContext.OEMNumber;
        setComponents([initialComponent]);
      }
      setKit(prev => {
        const nextDefault = deriveDefaultDescriptionRef.current();
        lastDefaultDescriptionRef.current = nextDefault;
        const next = { ...prev };
        // Carry over the Item Type / Fitment Location that was already chosen
        // before the kit editor opened (e.g. the target line picked in the
        // "Create Kit from Selected" builder) so it doesn't need reselecting.
        if (
          fitmentContext?.ItemTypeKey != null &&
          next.BaseItemTypeKey == null &&
          next.BaseKitItemTypeId == null
        ) {
          next.BaseItemTypeKey = fitmentContext.ItemTypeKey;
        }
        if (
          fitmentContext?.FitmentLocationKey != null &&
          next.BaseFitmentLocationKey == null &&
          next.BaseKitFitmentLocationId == null
        ) {
          next.BaseFitmentLocationKey = fitmentContext.FitmentLocationKey;
        }
        if (!(prev.Description && prev.Description.trim().length > 0)) {
          next.Description = nextDefault;
        }
        return next;
      });
      setHistory([]);
      setHistorySelection(null);
      return;
    }

    setBusy(true);
    setError('');
    (async () => {
      try {
        const detail = await api.fetchKit(kitId);
        hydrateKitDetail(detail);
        // Older kits were saved without a target location, which stops the refresh from
        // matching other models. Take it from the line the kit was opened from.
        const loadedKit = detail?.kit || {};
        const ctxItemTypeKey = fitmentContext?.ItemTypeKey ?? null;
        const ctxLocationKey = fitmentContext?.FitmentLocationKey ?? null;
        const kitLacksLocation =
          loadedKit.BaseFitmentLocationKey == null && loadedKit.BaseKitFitmentLocationId == null;
        const itemTypeCompatible =
          loadedKit.BaseKitItemTypeId == null &&
          (loadedKit.BaseItemTypeKey == null || Number(loadedKit.BaseItemTypeKey) === Number(ctxItemTypeKey));
        if (ctxItemTypeKey != null && ctxLocationKey != null && kitLacksLocation && itemTypeCompatible) {
          setKit((prev) => ({
            ...prev,
            BaseItemTypeKey: ctxItemTypeKey,
            BaseFitmentLocationKey: ctxLocationKey
          }));
          setInfo('Item Type and Fitment Location taken from the selected line. Save the kit to keep them.');
        }
        await refreshHistory(kitId);
        setHistoryModalError('');
      } catch (err) {
        console.error('Failed to load kit', err);
        setError(err?.response?.data?.error || 'Failed to load kit');
      } finally {
        setBusy(false);
      }
    })();
  }, [open, kitId, initialKitId, fitmentContext, initialComponents, hydrateKitDetail, refreshHistory]);

  useEffect(() => {
    if (!open) {
      hasAutoLoadedSuggestionsRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    hasAutoLoadedSuggestionsRef.current = false;
  }, [kitId]);

  useEffect(() => {
    if (!open) return;
    const shouldAutoLoad = Boolean(kitId || initialKitId);
    if (!shouldAutoLoad) return;
    if (!components.length) return;
    if (hasAutoLoadedSuggestionsRef.current) return;
    hasAutoLoadedSuggestionsRef.current = true;
    refreshReuseSuggestions({ silent: true }).catch(err => console.warn('Reuse suggestion failed', err));
  }, [open, kitId, initialKitId, components.length, refreshReuseSuggestions]);

  useEffect(() => {
    if (!open || kitId) return;
    const nextDefault = deriveDefaultDescription();
    if (!nextDefault) return;
    setKit(prev => {
      const current = (prev.Description || '').trim();
      const lastDefault = lastDefaultDescriptionRef.current;
      const shouldApply =
        !current ||
        current === 'New Kit' ||
        !lastDefault ||
        current === lastDefault;
      if (!shouldApply) {
        return prev;
      }
      if (current === nextDefault) {
        lastDefaultDescriptionRef.current = nextDefault;
        return prev;
      }
      lastDefaultDescriptionRef.current = nextDefault;
      return { ...prev, Description: nextDefault };
    });
  }, [open, kitId, deriveDefaultDescription]);

  useEffect(() => {
    localStorage.setItem('kitTolerance', JSON.stringify(tolerance));
  }, [tolerance]);

  const effectiveFitmentKey = fitmentContext?.FitmentKey ?? localFitmentKey ?? null;

  const addComponent = () => {
    setComponents(prev => [...prev, emptyComponent()]);
  };

  const removeComponent = (index) => {
    setComponents(prev => prev.filter((_, idx) => idx !== index));
  };

  const updateComponent = (index, field, value) => {
    setComponents(prev => prev.map((comp, idx) => {
      if (idx !== index) return comp;
      const next = { ...comp, [field]: value };
      if (field === 'SKUGroup') {
        next.SKUGroupKey = null;
        next.SkuOptions = [];
        next.SKUKey = null;
        next.SKU = '';
      }
      if (field === 'SKU') {
        next.SKUKey = null;
      }
      if (field === 'Quantity') {
        next.Quantity = value === '' ? null : value;
      }
      return next;
    }));
  };

  const onSelectBaseItemType = async (itemTypeKey) => {
    setSelectedItemTypeKey(itemTypeKey);
    if (!itemTypeKey) {
      setCoreFitmentLocations([]);
      setKit(prev => ({ ...prev, BaseItemTypeKey: null }));
      return;
    }
    setKit(prev => ({ ...prev, BaseItemTypeKey: itemTypeKey }));
    try {
      const locs = await api.listFitmentLocationsForItemType(itemTypeKey);
      setCoreFitmentLocations(locs);
    } catch (err) {
      console.warn('Failed to fetch fitment locations', err);
    }
  };

  const openCreateKitItemType = async () => {
    const name = window.prompt('New kit item type name');
    if (!name) return;
    try {
      const created = await api.createKitItemType({ name });
      setKitItemTypes(prev => [...prev, created]);
      handleKitItemTypeChange(`kit:${created.KitItemTypeId}`);
      setInfo(`Added kit item type ${created.Name}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create kit item type');
    }
  };

  const openCreateKitFitmentLocation = async () => {
    const name = window.prompt('New kit fitment location name');
    if (!name) return;
    try {
      const created = await api.createKitFitmentLocation({ name });
      setKitFitmentLocations(prev => [...prev, created]);
      handleKitFitmentLocationChange(`kit:${created.KitFitmentLocationId}`);
      setInfo(`Added kit fitment location ${created.Name}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create kit fitment location');
    }
  };

  const payloadFromState = () => ({
    kit: {
      description: kit.Description,
      skuGroupKey: kit.SKUGroupKey,
      baseItemTypeKey: kit.BaseItemTypeKey,
      baseKitItemTypeId: kit.BaseKitItemTypeId,
      baseFitmentLocationKey: kit.BaseFitmentLocationKey,
      baseKitFitmentLocationId: kit.BaseKitFitmentLocationId,
      fitmentInfo: kit.FitmentInfo
    },
    components: components.map((comp, index) => ({
      KitComponentId: comp.KitComponentId,
      ComponentOEMNumber: comp.ComponentOEMNumber,
      Quantity: Number(comp.Quantity) || 0,
      OEMNote: comp.OEMNote,
      OEMKey: comp.OEMKey,
      ItemTypeKey: comp.ItemTypeKey,
      FitmentLocationKey: comp.FitmentLocationKey,
      KitItemTypeId: comp.KitItemTypeId,
      KitFitmentLocationId: comp.KitFitmentLocationId,
      SKUGroupKey: comp.SKUGroupKey,
      SKUKey: comp.SKUKey,
      DisplayOrder: index + 1,
      SkuOptions: Array.isArray(comp.SkuOptions) ? comp.SkuOptions.map(opt => ({
        KitComponentSkuOptionId: opt.KitComponentSkuOptionId,
        SKUGroupKey: opt.SKUGroupKey,
        SKUKey: opt.SKUKey,
        SKU: opt.SKU || opt.sku,
        SKUCode: opt.SKUCode || opt.skuCode || opt.SKU || opt.sku || null,
        Description: opt.Description,
        UOM: opt.UOM,
        Price: opt.Price,
        IsDefault: !!opt.IsDefault,
        IsAllowed: opt.IsAllowed !== false
      })) : []
    })),
    productOEMs: productOEMs.map(po => ({
      OEMNumber: (po.OEMNumber || '').trim().toUpperCase(),
      OEMKey: po.OEMKey ?? null
    })).filter(po => po.OEMNumber)
  });

  const onSave = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromState();
      let resultingKitId = kitId;
      if (kitId) {
        const updated = await api.updateKit(kitId, payload);
        setInfo('Kit updated');
        resultingKitId = updated.kitId || kitId;
        setKitId(resultingKitId);
        const detail = await api.fetchKit(resultingKitId);
        hydrateKitDetail(detail);
        await refreshHistory(resultingKitId);
      } else {
        const created = await api.createKit(payload);
        if (created?.kitId) {
          resultingKitId = created.kitId;
          setKitId(created.kitId);
          setInfo(`Kit created (${created.kitId})`);
          setReuseCandidates(created.reuseCandidates || []);
          const detail = await api.fetchKit(created.kitId);
          hydrateKitDetail(detail);
          await refreshHistory(created.kitId);
        }
      }
      setHistoryModalError('');
      skuOptionsHydrationCacheRef.current.clear();
      if ((resultingKitId || kitId) && (effectiveFitmentKey || typeof ensureFitment === 'function')) {
        await onLinkToFitment(resultingKitId || kitId);
      }
      const savedKitId = resultingKitId || kitId;
      if (savedKitId) {
        // Same check as Kitting Manager's "Refresh Fitments": the refresh needs a core target.
        if (payload.kit.baseItemTypeKey && payload.kit.baseFitmentLocationKey) {
          setRefreshImporterKitId(savedKitId);
        } else {
          setInfo('Kit saved. Set a (non-kit) Item Type and Fitment Location to refresh other models with this kit.');
        }
      }
    } catch (err) {
      console.error('Kit save failed', err);
      setError(err?.response?.data?.error || 'Failed to save kit');
    } finally {
      setBusy(false);
    }
  };

  const onLinkToFitment = async (targetKitId = kitId) => {
    if (!targetKitId) return;
    let targetFitmentKey = fitmentContext?.FitmentKey ?? localFitmentKey ?? null;
    let ensureResult = null;

    if (!targetFitmentKey && typeof ensureFitment === 'function') {
      try {
        ensureResult = await ensureFitment(fitmentContext);
        targetFitmentKey = ensureResult?.fitmentKey ?? null;
        if (targetFitmentKey) {
          setLocalFitmentKey(targetFitmentKey);
        }
      } catch (err) {
        console.error('Failed to ensure fitment before linking kit:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to prepare fitment for kit.');
        return;
      }
    }

    if (!targetFitmentKey) {
      setError('Save or create the fitment before linking a kit.');
      return;
    }

    try {
      await api.applyKitToFitment(targetFitmentKey, {
        kitId: targetKitId,
        appendUnusedNote: reuseCandidates.some(c => c.evaluation?.extra?.length),
        unusedComponents: reuseCandidates.flatMap(c => c.evaluation?.extra || []),
        fitmentInfo: kit.FitmentInfo
      });
      // Awaited so this model's row is linked before the refresh preview reads the data.
      if (typeof onKitLinked === 'function') await onKitLinked(targetKitId, { fitmentKey: targetFitmentKey, ensureResult });
    } catch (err) {
      console.error('Failed to link kit to fitment', err);
      setError(err?.response?.data?.error || 'Failed to link kit');
    }
  };

  // ─── Unlink handlers ─────────────────────────────────────────────────────
  const onUnlinkFitment = async () => {
    const fk = effectiveFitmentKey;
    if (!fk || !kitId) return;
    if (!window.confirm(`Unlink fitment #${fk} from kit ${kitId}?`)) return;
    setBusy(true);
    setError('');
    try {
      await api.removeKitFromFitment(fk);
      setInfo(`Fitment #${fk} unlinked from kit.`);
      if (typeof onKitLinked === 'function') onKitLinked(null, { fitmentKey: fk });
      onClose();
    } catch (err) {
      console.error('Failed to unlink fitment from kit', err);
      setError(err?.response?.data?.error || 'Failed to unlink fitment');
    } finally {
      setBusy(false);
    }
  };

  const onUnlinkAll = async () => {
    if (!kitId) return;
    if (!window.confirm(`Unlink ALL fitments from kit ${kitId}? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.unlinkAllFitments(kitId);
      const count = result?.unlinked ?? 0;
      setInfo(`${count} fitment${count === 1 ? '' : 's'} unlinked from kit.`);
      if (typeof onKitLinked === 'function') onKitLinked(null, { fitmentKey: effectiveFitmentKey });
      onClose();
    } catch (err) {
      console.error('Failed to unlink all fitments from kit', err);
      setError(err?.response?.data?.error || 'Failed to unlink all fitments');
    } finally {
      setBusy(false);
    }
  };

  const onCheckStock = async () => {
    setStockBusy(true);
    setStockSummary(null);
    try {
      let token;
      try {
        token = await fetchOrderwiseToken();
      } catch (err) {
        console.error('Token error', err);
        setError(err?.response?.data?.error || 'OrderWise token request failed');
        return;
      }
      if (!token) {
        setError('OrderWise token not available. Please verify server credentials.');
        return;
      }
      const defaultSkus = components
        .map(comp => {
          const options = Array.isArray(comp.SkuOptions) ? comp.SkuOptions : [];
          const selected = options.find(opt => opt.IsDefault) || options.find(opt => opt.IsAllowed !== false);
          if (!selected) return null;
          return {
            sku: selected.SKU || selected.sku || selected.SKUCode,
            quantity: Number(comp.Quantity) || 0,
            component: comp
          };
        })
        .filter(Boolean)
        .filter(row => row.sku && row.quantity > 0);

      if (defaultSkus.length === 0) {
        setError('No default SKUs configured for components');
        return;
      }

      const responses = [];
      for (const row of defaultSkus) {
        try {
          const response = await postOrderwise('/fetch-sku-stock', [{ name: '@SKU', value: row.sku }], token, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 300000
          });
          const payload = Array.isArray(response) && response.length > 0 ? response[0] : null;
          responses.push({ row, oapi: payload });
        } catch (err) {
          console.warn('Stock fetch failed for', row.sku, err);
          responses.push({ row, oapi: null, error: err });
        }
      }

      const annotated = responses.map(entry => {
        const freeStock = Number(entry.oapi?.vasq_free_stock_quantity ?? 0);
        const builds = entry.row.quantity > 0 ? Math.floor(freeStock / entry.row.quantity) : 0;
        return {
          sku: entry.row.sku,
          quantityRequired: entry.row.quantity,
          freeStock,
          description: entry.oapi?.vad_description || '',
          uom: entry.oapi?.vad_uom || '',
          price: entry.oapi?.vfp_vafp_rsp_exc_vat || entry.oapi?.price || null,
          builds
        };
      });
      const maxBuilds = annotated.reduce((acc, entry) => Math.min(acc, entry.builds), Infinity);
      setStockSummary({ annotated, maxBuilds: Number.isFinite(maxBuilds) ? maxBuilds : 0 });
    } finally {
      setStockBusy(false);
    }
  };

  const diffSummary = useMemo(() => {
    if (!Array.isArray(reuseCandidates) || reuseCandidates.length === 0) return null;
    return reuseCandidates.map(candidate => {
      const evaluation = candidate?.evaluation || {};
      const extra = Array.isArray(evaluation.extra) ? evaluation.extra : [];
      const missing = Array.isArray(evaluation.missing) ? evaluation.missing : [];
      const quantityDeltas = Array.isArray(evaluation.quantityDeltas) ? evaluation.quantityDeltas : [];
      const differences = Array.isArray(evaluation.differences) ? evaluation.differences : [];
      const hasAnyDiff = extra.length > 0 || missing.length > 0 || quantityDeltas.length > 0 || differences.length > 0;
      const isExact = Boolean(evaluation.isExactMatch || (!hasAnyDiff && evaluation.score === 1));
      const type = isExact
        ? 'exact'
        : evaluation.isSuperset
          ? 'superset'
          : evaluation.isSubset
            ? 'subset'
            : 'similar';

      return {
        kitId: candidate?.kitId,
        description: candidate?.description,
        extra,
        missing,
        quantityDeltas,
        differences,
        hasAnyDiff,
        type
      };
    });
  }, [reuseCandidates]);

  const headerItemTypeLabel = resolveItemTypeLabel();
  const headerFitmentLocationLabel = resolveFitmentLocationLabel();
  const headerContextLabel = [headerItemTypeLabel, headerFitmentLocationLabel].filter(Boolean).join(' • ');
  const headerTitle = kitId ? `Kit ${kitId}` : 'New Kit';
  const headerText = headerContextLabel ? `${headerTitle} — ${headerContextLabel}` : headerTitle;
  const activeOptionsComponent = skuOptionsModal.open && skuOptionsModal.componentIndex !== null
    ? components[skuOptionsModal.componentIndex] ?? null
    : null;
  const activeOptions = Array.isArray(activeOptionsComponent?.SkuOptions) ? activeOptionsComponent.SkuOptions : [];
  const defaultRadioName = skuOptionsModal.componentIndex != null
    ? `sku-default-${skuOptionsModal.componentIndex}`
    : 'sku-default';
  const activeComponentLabel = activeOptionsComponent
    ? [
        activeOptionsComponent.ComponentOEMNumber,
        activeOptionsComponent.SKUGroup,
        activeOptionsComponent.SKU
      ]
        .map(part => (part || '').toString().trim())
        .filter(Boolean)
        .join(' • ') || `Component ${(skuOptionsModal.componentIndex ?? 0) + 1}`
    : '';

  const formatDateTime = useCallback((value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    } catch {
      /* ignore */
    }
    return typeof value === 'string' ? value : String(value ?? '');
  }, []);

  const formatPrice = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '—';
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return formatMoney(numeric);
    }
    return String(value);
  }, []);

  const copyModalRows = Array.isArray(copyModalState.results) ? copyModalState.results : [];
  const copyModalTotalRows = copyModalState.total || 0;
  const copyModalTotalPages = Math.max(1, Math.ceil(Math.max(copyModalTotalRows, 0) / copyPageSize));
  const copyModalRangeStart = copyModalTotalRows === 0 ? 0 : (copyModalState.page - 1) * copyPageSize + 1;
  const copyModalRangeEnd = copyModalTotalRows === 0
    ? 0
    : Math.min(copyModalTotalRows, copyModalState.page * copyPageSize);
  const copyModalHasResults = copyModalRows.length > 0;
  const copyModalSearchDraft = (copyModalState.searchDraft || '').trim();
  const copyModalSearchValue = (copyModalState.search || '').trim();
  const copyModalHasSearch = !!copyModalSearchValue;
  const copyModalCanClear = copyModalHasSearch || !!copyModalSearchDraft;
  const copyModalIsLoading = !!copyModalState.loading;
  const copyModalCopyingId = copyModalState.copyingId;

  if (!open) return null;

  return (
    <>
      <div className="kitting-modal-backdrop">
      <div className="kitting-modal">
        <header className="kitting-modal__header">
          <div className="kitting-modal__header-title">
            <strong>{headerText}</strong>
            {effectiveFitmentKey && (
              <span className="kitting-modal__context">Fitment #{effectiveFitmentKey}</span>
            )}
          </div>
          <div className="kitting-modal__actions">
            {canModify && (
              <button
                type="button"
                className="kitting-modal__button-secondary"
                onClick={openManageOEMFromHeader}
                disabled={!kitId}
              >
                Manage OEM
              </button>
            )}
            {canModify && (
              <button
                type="button"
                className="kitting-modal__button-secondary"
                onClick={openCopyModal}
              >
                Copy From Kit
              </button>
            )}
            <button
              type="button"
              className="kitting-modal__button-secondary"
              onClick={openHistoryModal}
            >
              View History
            </button>
            {canModify && (
              <button type="button" onClick={onSave} disabled={busy}>
                {kitId ? 'Save Kit' : 'Create Kit'}
              </button>
            )}
            {canModify && kitId && effectiveFitmentKey && (
              <button
                type="button"
                className="kitting-modal__button-secondary"
                style={{ color: '#fca5a5', borderColor: '#fca5a5' }}
                onClick={onUnlinkFitment}
                disabled={busy}
                title={`Unlink fitment #${effectiveFitmentKey} from this kit`}
              >
                Unlink Fitment
              </button>
            )}
            {canModify && kitId && (
              <button
                type="button"
                className="kitting-modal__button-secondary"
                style={{ color: '#fca5a5', borderColor: '#fca5a5' }}
                onClick={onUnlinkAll}
                disabled={busy}
                title="Unlink ALL fitments from this kit"
              >
                Unlink All
              </button>
            )}
            <button type="button" className="kitting-modal__button-secondary" onClick={onClose}>Close</button>
          </div>
        </header>

        {(error || info) && (
          <div className="kitting-modal__messages">
            {error && <div className="kitting-modal__error">{error}</div>}
            {info && <div className="kitting-modal__info">{info}</div>}
          </div>
        )}

        <section className="kitting-modal__body">
          <div className="kitting-modal__form-grid">
            <label style={{ position: 'relative' }}>
              Kit SKU Group
              <input
                type="text"
                placeholder="— Type to search SKU Group —"
                value={kitSkuGroupSearch}
                readOnly={!canModify}
                onChange={(e) => {
                  if (!canModify) return;
                  setKitSkuGroupSearch(e.target.value);
                  if (kit.SKUGroupKey) setKit(prev => ({ ...prev, SKUGroupKey: null, SKUGroup: DEFAULT_SKU_GROUP_LABEL }));
                }}
                onDoubleClick={() => {
                  if (!canModify) return;
                  setKit(prev => ({ ...prev, SKUGroupKey: null, SKUGroup: DEFAULT_SKU_GROUP_LABEL }));
                  setKitSkuGroupSearch('');
                  setShowKitSkuGroupDropdown(true);
                }}
                onFocus={() => {
                  if (!canModify) return;
                  setShowKitSkuGroupDropdown(true);
                  if (kit.SKUGroupKey && !kitSkuGroupSearch.trim()) loadKitSkuGroupName(kit.SKUGroupKey);
                }}
                onBlur={() => !canModify ? undefined : setTimeout(() => setShowKitSkuGroupDropdown(false), 300)}
                title={kit.SKUGroupKey ? `Group #${kit.SKUGroupKey}` : ''}
              />
              {showKitSkuGroupDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '280px',
                    overflowY: 'auto',
                    background: 'var(--modal-bg-white-cell, #fff)',
                    border: '1px solid var(--modal-input-border-gray, #d1d5db)',
                    borderTop: 'none',
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {/* ── SKU→Group suggestions ── */}
                  {kitSkuGroupSearch.trim().length >= 2 && kitSkuLookupDone && kitSkuLookupKey && (
                    <div style={{ borderBottom: '2px solid var(--modal-border-gray, #e5e7eb)' }}>
                      <div style={{ padding: '6px 8px', fontSize: '11px', fontWeight: 600, color: 'var(--modal-text-gray, #6b7280)', background: 'var(--modal-bg-gray, #f3f4f6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        SKU "{kitSkuGroupSearch.trim()}" belongs to:
                      </div>
                      {kitSkuLookupGroups.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {kitSkuLookupGroups.map((g) => (
                            <li
                              key={`kit-sku-grp-${g.SKUGroupKey}`}
                              onClick={(e) => { e.stopPropagation(); handleKitUseExistingGroup(g); }}
                              style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid var(--modal-border-gray, #e5e7eb)', background: kit.SKUGroupKey === g.SKUGroupKey ? 'var(--modal-bg-gray, #f3f4f6)' : 'transparent', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600, background: '#22c55e', color: '#fff' }}>USE</span>
                              {g.SKUGroup} {g.SKUGroupDescription ? `(${g.SKUGroupDescription})` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ padding: '8px', fontSize: '12px', color: 'var(--modal-text-gray, #6b7280)' }}>
                          This SKU is not in any group yet.
                        </div>
                      )}
                      {!kitCreateGroupMode ? (
                        <div style={{ padding: '6px 8px' }}>
                          <button
                            type="button"
                            onClick={() => { setKitCreateGroupMode(true); setKitNewGroupName(kitSkuGroupSearch.trim().replace(/[A-Za-z]+$/, '') || kitSkuGroupSearch.trim()); setKitCreateGroupError(''); setKitMergeWarning(null); }}
                            style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--modal-input-border-gray, #d1d5db)', background: 'var(--modal-bg-gray, #f3f4f6)', cursor: 'pointer', color: 'inherit' }}
                          >
                            + Create New Group for this SKU
                          </button>
                        </div>
                      ) : (
                        <div style={{ padding: '8px', borderTop: '1px solid var(--modal-border-gray, #e5e7eb)' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div>
                              <label style={{ fontSize: '11px', color: 'var(--modal-text-gray, #6b7280)', display: 'block', marginBottom: '2px' }}>New Group Name</label>
                              <input type="text" value={kitNewGroupName} onChange={(e) => { setKitNewGroupName(e.target.value); setKitMergeWarning(null); }} style={{ padding: '4px 6px', border: '1px solid var(--modal-input-border-gray, #d1d5db)', borderRadius: '4px', width: '140px', fontSize: '12px' }} placeholder="e.g. 011111" />
                            </div>
                            <button type="button" disabled={kitCreateGroupBusy || !kitNewGroupName.trim()} onClick={() => handleKitCreateGroupForSku(kitNewGroupName)} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '4px', border: 'none', background: '#2563eb', color: '#fff', cursor: kitCreateGroupBusy || !kitNewGroupName.trim() ? 'not-allowed' : 'pointer', opacity: kitCreateGroupBusy || !kitNewGroupName.trim() ? 0.5 : 1 }}>
                              {kitCreateGroupBusy ? 'Creating…' : 'Create & Use'}
                            </button>
                            <button type="button" onClick={() => { setKitCreateGroupMode(false); setKitMergeWarning(null); setKitCreateGroupError(''); }} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--modal-input-border-gray, #d1d5db)', background: 'transparent', cursor: 'pointer', color: 'inherit' }}>
                              Cancel
                            </button>
                          </div>
                          {kitMergeWarning && (
                            <div style={{ marginTop: '6px', padding: '6px 8px', background: '#fef3c7', borderRadius: '4px', fontSize: '12px', color: '#92400e' }}>
                              A group named "<strong>{kitMergeWarning.existingName}</strong>" already exists (Key: {kitMergeWarning.existingKey}).
                              <div style={{ marginTop: '4px', display: 'flex', gap: '6px' }}>
                                <button type="button" onClick={handleKitMergeConfirm} disabled={kitCreateGroupBusy} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '3px', border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer' }}>Use Existing Group</button>
                                <button type="button" onClick={() => setKitMergeWarning(null)} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '3px', border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: 'inherit' }}>Change Name</button>
                              </div>
                            </div>
                          )}
                          {kitCreateGroupError && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: '#dc2626' }}>{kitCreateGroupError}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* ── Loading indicator for SKU lookup ── */}
                  {kitSkuGroupSearch.trim().length >= 2 && kitSkuLookupBusy && (
                    <div style={{ padding: '6px 8px', fontSize: '11px', color: 'var(--modal-text-gray, #6b7280)', fontStyle: 'italic', borderBottom: '1px solid var(--modal-border-gray, #e5e7eb)' }}>
                      Looking up SKU…
                    </div>
                  )}
                  {/* ── Normal SKU Group search results ── */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {kitSkuGroupBusy ? (
                      <li style={{ padding: '8px', color: 'var(--modal-text-gray, #6b7280)', fontStyle: 'italic' }}>Searching groups…</li>
                    ) : kitSkuGroupResults.length === 0 && !kitSkuLookupDone ? (
                      <li style={{ padding: '8px', color: 'var(--modal-text-gray, #6b7280)', fontStyle: 'italic' }}>
                        {kitSkuGroupSearch.trim() ? 'No matching groups' : 'Type to search'}
                      </li>
                    ) : (
                      kitSkuGroupResults.map((g) => (
                        <li
                          key={`kit-grp-${g.SKUGroupKey}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setKit(prev => ({ ...prev, SKUGroupKey: g.SKUGroupKey, SKUGroup: normalizeSkuGroupLabel(g.SKUGroup || '') }));
                            setKitSkuGroupSearch(g.SKUGroup || '');
                            setShowKitSkuGroupDropdown(false);
                            setKitSkuGroupResults([]);
                          }}
                          style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid var(--modal-border-gray, #e5e7eb)', background: kit.SKUGroupKey === g.SKUGroupKey ? 'var(--modal-bg-gray, #f3f4f6)' : 'transparent' }}
                        >
                          {g.SKUGroup} {g.SKUGroupDescription ? `— ${g.SKUGroupDescription}` : ''}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </label>
            <label>
              Description
              <input
                value={kit.Description || ''}
                onChange={e => setKit(prev => ({ ...prev, Description: e.target.value }))}
                disabled={!canModify}
              />
            </label>
            <label>
              Manage Item Types
              <div className="kitting-modal__inline">
                <select
                  value={getItemTypeOptionKey(kit.BaseItemTypeKey, kit.BaseKitItemTypeId)}
                  disabled={!canModify}
                  onChange={e => handleKitItemTypeChange(e.target.value)}
                >
                  <option value="">--</option>
                  {itemTypeOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}{opt.source === 'kit' ? ' (Kit)' : ''}
                    </option>
                  ))}
                </select>
                {canModify && (
                  <button type="button" onClick={openCreateKitItemType}>+</button>
                )}
              </div>
            </label>
            <label>
              Manage Fitment Locations
              <div className="kitting-modal__inline">
                <select
                  value={getFitmentLocationOptionKey(kit.BaseFitmentLocationKey, kit.BaseKitFitmentLocationId)}
                  disabled={!canModify}
                  onChange={e => handleKitFitmentLocationChange(e.target.value)}
                >
                  <option value="">--</option>
                  {getFitmentLocationOptionsForItemType(kit.BaseItemTypeKey).map(opt => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}{opt.source === 'kit' ? ' (Kit)' : ''}
                    </option>
                  ))}
                </select>
                {canModify && (
                  <button type="button" onClick={openCreateKitFitmentLocation}>+</button>
                )}
              </div>
            </label>
          </div>
          <label className="kitting-modal__fitment-info">
            Fitment Info
            <textarea
              value={kit.FitmentInfo || ''}
              onChange={e => setKit(prev => ({ ...prev, FitmentInfo: e.target.value }))}
              disabled={!canModify}
              placeholder="Optional notes to store on the fitment when this kit is linked"
            />
          </label>

          {/* ── Product OEM Numbers (Kit OEM Mode) ───────────────────── */}
          <div style={{ marginTop: '0.25rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.92rem' }}>
              Product OEM Numbers
              <span style={{ fontWeight: 400, fontSize: '0.82rem', marginLeft: '0.5rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                (same-level kits — matches existing fitments by these OEMs)
              </span>
            </label>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
              padding: '0.4rem 0.5rem',
              border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
              borderRadius: '6px',
              background: isDark ? '#1e1e1e' : '#fff',
              minHeight: '2.2rem', alignItems: 'center', marginTop: '0.25rem'
            }}>
              {productOEMs.map((po, idx) => (
                <span key={idx} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  background: isDark ? '#374151' : '#e5e7eb',
                  color: isDark ? '#e5e7eb' : '#1f2937',
                  borderRadius: '4px', padding: '0.15rem 0.5rem', fontSize: '0.88rem'
                }}>
                  {po.OEMNumber}
                  {canModify && (
                    <button
                      type="button"
                      onClick={() => setProductOEMs(prev => prev.filter((_, i) => i !== idx))}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: isDark ? '#ef4444' : '#dc2626', fontWeight: 700,
                        fontSize: '0.95rem', lineHeight: 1, padding: 0
                      }}
                      title="Remove"
                    >×</button>
                  )}
                </span>
              ))}
              {canModify && (
                <input
                  type="text"
                  value={productOEMInput}
                  onChange={e => setProductOEMInput(e.target.value)}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ',') && productOEMInput.trim()) {
                      e.preventDefault();
                      const val = productOEMInput.trim().toUpperCase();
                      if (val && !productOEMs.some(po => po.OEMNumber === val)) {
                        setProductOEMs(prev => [...prev, { OEMNumber: val, OEMKey: null }]);
                      }
                      setProductOEMInput('');
                    }
                    if (e.key === 'Backspace' && !productOEMInput && productOEMs.length > 0) {
                      setProductOEMs(prev => prev.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    const val = productOEMInput.trim().toUpperCase();
                    if (val && !productOEMs.some(po => po.OEMNumber === val)) {
                      setProductOEMs(prev => [...prev, { OEMNumber: val, OEMKey: null }]);
                    }
                    setProductOEMInput('');
                  }}
                  placeholder={productOEMs.length === 0 ? 'Type an OEM number and press Enter…' : ''}
                  style={{
                    border: 'none', outline: 'none', background: 'transparent',
                    flex: '1 1 6rem', minWidth: '6rem', fontSize: '0.88rem',
                    color: isDark ? '#e5e7eb' : '#1f2937'
                  }}
                />
              )}
            </div>
            {productOEMs.length > 0 && (
              <div style={{ fontSize: '0.78rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.2rem' }}>
                Kit OEM Mode — refresh will search for existing fitments matching these product OEMs.
              </div>
            )}
          </div>

          <section className="kitting-modal__components">
            <header>
              <h4>Components</h4>
              {canModify && <button type="button" onClick={addComponent}>Add Component</button>}
            </header>
            <div className="kitting-modal__table">
              <div className="kitting-modal__row kitting-modal__row--head">
                <span>OEM</span>
                <span>Qty</span>
                <span>Item Type</span>
                <span>Fitment Location</span>
                <span>SKU Group</span>
                <span>SKU</span>
                <span>OEM Notes</span>
                <span>SKUs</span>
                <span></span>
              </div>
              {components.map((comp, idx) => {
                const options = Array.isArray(comp.SkuOptions) ? comp.SkuOptions : [];
                const defaultOption = options.find(opt => opt.IsDefault);
                const allowedOptions = options.filter(opt => opt.IsAllowed !== false);
                const allowedCount = allowedOptions.length;
                const optionsCount = options.length;
                const summaryLines = optionsCount > 0
                  ? [
                      defaultOption ? `Default: ${defaultOption.SKU || '—'}` : null,
                      `${optionsCount} option${optionsCount === 1 ? '' : 's'} (${allowedCount} allowed)`
                    ].filter(Boolean)
                  : ['No options stored — use Manage SKU to add'];
                const allowedSkuList = allowedOptions
                  .map(opt => (opt.SKU || opt.SKUCode || '').trim())
                  .filter(Boolean)
                  .join(', ');
                const fallbackSkuList = optionsCount > 0 && !allowedSkuList
                  ? options
                      .map(opt => (opt.SKU || opt.SKUCode || '').trim())
                      .filter(Boolean)
                      .join(', ')
                  : '';
                const skuInputValue = allowedSkuList || comp.SKU || '';
                const skuTitle = allowedSkuList
                  ? `Allowed: ${allowedSkuList}`
                  : fallbackSkuList
                    ? `Available: ${fallbackSkuList}`
                    : comp.SKUKey ? `SKU #${comp.SKUKey}` : 'SKU resolves from selected group';

                return (
                  <div key={idx} className="kitting-modal__row">
                    <input
                      value={comp.ComponentOEMNumber || ''}
                      disabled={!canModify}
                      onChange={e => updateComponent(idx, 'ComponentOEMNumber', e.target.value)}
                      onBlur={e => handleComponentOEMBlur(idx, e.target.value)}
                      onFocus={() => setActiveComponentIndex(idx)}
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={comp.Quantity ?? ''}
                      disabled={!canModify}
                      onChange={e => updateComponent(idx, 'Quantity', e.target.value)}
                      onFocus={() => setActiveComponentIndex(idx)}
                    />
                    <select
                      value={getItemTypeOptionKey(comp.ItemTypeKey, comp.KitItemTypeId)}
                      disabled={!canModify}
                      onChange={e => handleComponentItemTypeChange(idx, e.target.value)}
                      onFocus={() => setActiveComponentIndex(idx)}
                    >
                      <option value="">--</option>
                      {itemTypeOptions.map(opt => (
                        <option key={`${opt.key}-${idx}`} value={opt.key}>
                          {opt.label}{opt.source === 'kit' ? ' (Kit)' : ''}
                        </option>
                      ))}
                    </select>
                    <select
                      value={getFitmentLocationOptionKey(comp.FitmentLocationKey, comp.KitFitmentLocationId)}
                      disabled={!canModify}
                      onChange={e => handleComponentFitmentLocationChange(idx, e.target.value)}
                      onFocus={() => setActiveComponentIndex(idx)}
                    >
                      <option value="">--</option>
                      {getFitmentLocationOptionsForItemType(comp.ItemTypeKey).map(opt => (
                        <option key={`${opt.key}-${idx}`} value={opt.key}>
                          {opt.label}{opt.source === 'kit' ? ' (Kit)' : ''}
                        </option>
                      ))}
                    </select>
                    <div
                      className="kitting-modal__field-readonly"
                      title={comp.SKUGroupKey ? `Group #${comp.SKUGroupKey}` : ''}
                    >
                      {comp.SKUGroup || DEFAULT_SKU_GROUP_LABEL}
                    </div>
                    <textarea
                      className="kitting-modal__sku-display"
                      value={skuInputValue}
                      readOnly
                      placeholder="Resolved from group"
                      title={skuTitle}
                      rows={2}
                      onFocus={() => setActiveComponentIndex(idx)}
                    />
                    <textarea
                      value={comp.OEMNote || ''}
                      disabled={!canModify}
                      onChange={e => updateComponent(idx, 'OEMNote', e.target.value)}
                      onFocus={() => setActiveComponentIndex(idx)}
                    />
                    <div className="kitting-modal__sku-options">
                      {summaryLines.map((line, lineIdx) => (
                        <span key={lineIdx} className="kitting-modal__sku-options-line">{line}</span>
                      ))}
                      <button type="button" className="kitting-modal__button-secondary" onClick={() => openSkuOptionsModal(idx)}>
                        View Options
                      </button>
                    </div>
                    <div className="kitting-modal__row-actions">
                      {canModify && (
                        <>
                          <button type="button" onClick={() => openManageOEMForComponent(idx)}>Manage OEM</button>
                          <button type="button" onClick={() => openManageSKUForComponent(idx)}>Manage SKU</button>
                          <button type="button" onClick={() => removeComponent(idx)}>Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </section>

        <section className="kitting-modal__foot">
          <div className="kitting-modal__tolerance">
            <label>
              Missing tolerance
              <input
                type="number"
                min="0"
                max="0"
                step="1"
                value={tolerance.subsetMissingTolerance}
                onChange={e => setTolerance(prev => normalizeTolerance({
                  ...prev,
                  subsetMissingTolerance: e.target.value
                }))}
              />
            </label>
            <label>
              Quantity tolerance
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={tolerance.subsetQuantityTolerance}
                onChange={e => setTolerance(prev => normalizeTolerance({
                  ...prev,
                  subsetQuantityTolerance: e.target.value
                }))}
              />
            </label>
          </div>
          <div className="kitting-modal__stock">
            <button type="button" onClick={onCheckStock} disabled={stockBusy}>Check Stock</button>
            {stockSummary && (
              <div className="kitting-modal__stock-summary">
                <div><strong>Max buildable:</strong> {stockSummary.maxBuilds}</div>
                <ul>
                  {stockSummary.annotated.map(entry => (
                    <li key={entry.sku}>
                      {entry.sku}: stock {entry.freeStock} / required {entry.quantityRequired} (builds {entry.builds})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="kitting-modal__foot-actions">
            <button type="button" onClick={() => refreshReuseSuggestions()} disabled={components.length === 0}>
              Load Suggestions
            </button>
            <button type="button" onClick={() => setReuseCandidates([])} disabled={reuseCandidates.length === 0}>
              Clear Suggestions
            </button>
          </div>
        </section>

        {diffSummary && diffSummary.length > 0 && (
          <section className="kitting-modal__diff">
            <h4>Similar Kits</h4>
            {diffSummary.map(diff => (
              <div key={diff.kitId} className="kitting-modal__diff-row">
                <div className="kitting-modal__diff-header">
                  <span>{diff.kitId}</span>
                  <span>{diff.description}</span>
                  <span className={`kitting-modal__pill kitting-modal__pill--${diff.type}`}>
                    {diff.type.toUpperCase()}
                  </span>
                </div>
                {diff.hasAnyDiff && (
                  <ul>
                    {diff.missing.map(item => (
                      <li key={`missing-${diff.kitId}-${item.oem}`}>Missing {item.oem} ({item.requiredQuantity})</li>
                    ))}
                    {diff.extra.map(item => (
                      <li key={`extra-${diff.kitId}-${item.oem}`}>Extra {item.oem} ({item.candidateQuantity})</li>
                    ))}
                    {diff.quantityDeltas.map(item => (
                      <li key={`delta-${diff.kitId}-${item.oem}`}>
                        Quantity delta {item.oem}: need {item.requiredQuantity}, candidate {item.candidateQuantity}
                      </li>
                    ))}
                    {diff.differences.map((item, idx) => (
                      <li key={`diff-${diff.kitId}-${idx}`}>
                        {item.label || item.message || JSON.stringify(item)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
    <ManageOEMModal
      open={manageOEMState.open}
      onClose={closeManageOEM}
      initialOEMNumber={manageOEMState.initialNumber}
      initialOEMKey={manageOEMState.initialKey}
      canEdit={canEdit}
      onApply={applyManageOEMSelection}
    />
    <ManageSKUModal
      open={manageSKUState.open}
      onClose={closeManageSKU}
      initialSKUGroup={manageSKUState.initialGroup}
      initialSKU={manageSKUState.initialSku}
      isDark={isDark}
      canEdit={canEdit}
      onApply={applyManageSKUSelection}
    />
    {copyModalState.open && (
      <div className="kitting-modal__options-backdrop" onClick={closeCopyModal}>
        <div className="kitting-modal__copy-modal" onClick={(e) => e.stopPropagation()}>
          <header className="kitting-modal__copy-header">
            <div>
              <strong>Copy Existing Kit</strong>
              <div className="kitting-modal__copy-subtitle">
                {kitId
                  ? `Copying will replace the current ${kitId ? `Kit ${kitId}` : 'draft'} with the selected kit.`
                  : 'Copy an existing kit to start a new draft with pre-filled components.'}
              </div>
            </div>
            <button type="button" className="kitting-modal__button-secondary" onClick={closeCopyModal}>Close</button>
          </header>
          <div className="kitting-modal__copy-body">
            <label className="kitting-modal__copy-search">
              <span>Search kits</span>
              <div className="kitting-modal__copy-search-row">
                <input
                  type="text"
                  value={copyModalState.searchDraft}
                  onChange={(e) => handleCopySearchInput(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      applyCopySearch();
                    }
                  }}
                  placeholder="Kit ID, description, OEM, or SKU"
                  autoFocus
                />
                <button type="button" onClick={applyCopySearch} disabled={copyModalIsLoading}>Search</button>
                <button
                  type="button"
                  className="kitting-modal__button-secondary"
                  onClick={resetCopySearch}
                  disabled={!copyModalCanClear}
                >
                  Clear
                </button>
              </div>
            </label>
            <div className="kitting-modal__copy-hint">Only active kits are listed. Search across kit IDs, descriptions, and component values.</div>
            {copyModalState.error && (
              <div className="kitting-modal__copy-message is-error">{copyModalState.error}</div>
            )}
            {!copyModalState.error && copyModalIsLoading && (
              <div className="kitting-modal__copy-message is-loading">Loading kits…</div>
            )}
            {!copyModalState.error && !copyModalIsLoading && !copyModalHasResults && (
              <div className="kitting-modal__copy-empty">
                {copyModalHasSearch ? 'No kits match the current search.' : 'No kits available to copy.'}
              </div>
            )}
            {copyModalHasResults && (
              <div className="kitting-modal__copy-results">
                <table className="kitting-modal__copy-table">
                  <thead>
                    <tr>
                      <th>Kit</th>
                      <th>Description</th>
                      <th>Components</th>
                      <th>Usage</th>
                      <th>Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {copyModalRows.map((row, idx) => {
                      const rowId = row?.KitId ?? row?.kitId ?? row?.ID ?? row?.id ?? null;
                      const description = row?.Description || row?.description || (rowId ? `Kit ${rowId}` : 'Untitled Kit');
                      const itemType = row?.ItemType || row?.BaseItemType || row?.ItemTypeName || '';
                      const location = row?.FitmentLocation || row?.BaseFitmentLocation || row?.FitmentLocationName || '';
                      const updatedAt = row?.UpdatedAt || row?.updatedAt || row?.Updated || row?.updated || row?.ModifiedAt || row?.modifiedAt || '';
                      const componentCount = row?.ComponentCount ?? row?.LineCount ?? row?.UniqueComponentCount ?? row?.components ?? '';
                      const usageCount = row?.UsageCount ?? row?.usageCount ?? row?.FitmentUsage ?? '';
                      const isCopying = !!rowId && copyModalCopyingId === rowId;
                      const subtitle = [itemType, location].filter(Boolean).join(' • ');
                      return (
                        <tr key={rowId ?? `copy-row-${idx}`}>
                          <td>{rowId ?? '—'}</td>
                          <td>
                            <div className="kitting-modal__copy-row-title">{description}</div>
                            <div className="kitting-modal__copy-row-subtitle">{subtitle || '—'}</div>
                          </td>
                          <td className="kitting-modal__copy-cell-number">{componentCount ?? '—'}</td>
                          <td className="kitting-modal__copy-cell-number">{usageCount ?? '—'}</td>
                          <td>{formatDateTime(updatedAt) || '—'}</td>
                          <td className="kitting-modal__copy-actions">
                            <button
                              type="button"
                              onClick={() => rowId && handleCopyKit(rowId)}
                              disabled={!rowId || isCopying || copyModalIsLoading}
                            >
                              {isCopying ? 'Copying…' : 'Copy'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="kitting-modal__copy-pagination">
              <div>
                {copyModalTotalRows === 0
                  ? 'No kits to display'
                  : `Showing ${copyModalRangeStart}-${copyModalRangeEnd} of ${copyModalTotalRows}`}
              </div>
              <div className="kitting-modal__copy-pagination-controls">
                <button
                  type="button"
                  onClick={() => handleCopyPageChange('prev')}
                  disabled={copyModalState.page <= 1 || copyModalIsLoading}
                >
                  Prev
                </button>
                <span>Page {copyModalTotalRows === 0 ? 1 : copyModalState.page} of {copyModalTotalPages}</span>
                <button
                  type="button"
                  onClick={() => handleCopyPageChange('next')}
                  disabled={copyModalRangeEnd >= copyModalTotalRows || copyModalIsLoading}
                >
                  Next
                </button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    )}
    {historyModalOpen && (
      <div className="kitting-modal__options-backdrop" onClick={closeHistoryModal}>
        <div
          style={{
            background: isDark ? '#1e1e1e' : '#fff',
            color: isDark ? '#f0f0f0' : '#222',
            maxWidth: 'min(1050px, 92vw)',
            width: '100%',
            maxHeight: '85vh',
            borderRadius: '12px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            boxShadow: '0 18px 40px rgba(0,0,0,0.35)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Kit History</strong>
              <div style={{ fontSize: '0.9rem', color: isDark ? '#bdbdbd' : '#555' }}>
                {kitId ? `Kit ${kitId}` : 'Unsaved kit — history begins after the first save.'}
              </div>
            </div>
            <button type="button" className="kitting-modal__button-secondary" onClick={closeHistoryModal}>Close</button>
          </header>

          {historyModalError && (
            <div style={{ color: '#c44747', background: isDark ? '#2d1111' : '#fdeaea', padding: '0.65rem 0.75rem', borderRadius: '6px' }}>
              {historyModalError}
            </div>
          )}

          {history.length === 0 ? (
            <div style={{ fontStyle: 'italic', color: isDark ? '#bdbdbd' : '#555' }}>
              History will appear once changes are saved.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1rem', minHeight: '360px', flex: '1 1 auto' }}>
              <aside
                style={{
                  width: '280px',
                  borderRight: `1px solid ${isDark ? '#333' : '#e1e4e8'}`,
                  paddingRight: '0.75rem',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}
              >
                {history.map((entry) => {
                  const changedAt = entry.ChangedAt ? new Date(entry.ChangedAt).toLocaleString() : 'Unknown';
                  const isSelected = historySelection?.KitAuditLogId === entry.KitAuditLogId;
                  return (
                    <button
                      type="button"
                      key={entry.KitAuditLogId}
                      onClick={() => setHistorySelection(entry)}
                      style={{
                        textAlign: 'left',
                        padding: '0.65rem 0.75rem',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        background: isSelected ? (isDark ? '#2f3c61' : '#e6efff') : (isDark ? '#272727' : '#f6f8fa'),
                        color: 'inherit',
                        boxShadow: isSelected ? (isDark ? '0 0 0 1px #5c79ff inset' : '0 0 0 1px #4676ff inset') : 'none'
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{entry.Action || 'Change'}</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>{changedAt}</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>{entry.ChangedBy || '—'}</div>
                      {entry.Context && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>{entry.Context}</div>
                      )}
                    </button>
                  );
                })}
              </aside>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>
                {historySelection ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <div><strong>Action:</strong> {historySelection.Action || 'Change'}</div>
                      <div><strong>Changed By:</strong> {historySelection.ChangedBy || '—'}</div>
                      <div><strong>Changed At:</strong> {historySelection.ChangedAt ? new Date(historySelection.ChangedAt).toLocaleString() : '—'}</div>
                      {historySelection.Context && <div><strong>Context:</strong> {historySelection.Context}</div>}
                    </div>
                    {canModify && (
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          type="button"
                          onClick={() => handleHistoryApply(historySelection, 'before')}
                          disabled={!historySelection.BeforeState || historyApplyBusyId === historySelection.KitAuditLogId}
                        >
                          {historyApplyBusyId === historySelection.KitAuditLogId ? 'Applying…' : 'Restore (Undo)'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleHistoryApply(historySelection, 'after')}
                          disabled={!historySelection.AfterState || historyApplyBusyId === historySelection.KitAuditLogId}
                        >
                          {historyApplyBusyId === historySelection.KitAuditLogId ? 'Applying…' : 'Reapply (Redo)'}
                        </button>
                      </div>
                    )}
                    <section style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                      <div style={{ border: `1px solid ${isDark ? '#333' : '#e1e4e8'}`, borderRadius: '8px', padding: '0.75rem', background: isDark ? '#252525' : '#fafafa' }}>
                        <h5 style={{ margin: '0 0 0.5rem' }}>Before</h5>
                        {renderHistoryState(historySelection.BeforeState)}
                      </div>
                      <div style={{ border: `1px solid ${isDark ? '#333' : '#e1e4e8'}`, borderRadius: '8px', padding: '0.75rem', background: isDark ? '#252525' : '#fafafa' }}>
                        <h5 style={{ margin: '0 0 0.5rem' }}>After</h5>
                        {renderHistoryState(historySelection.AfterState)}
                      </div>
                    </section>
                  </>
                ) : (
                  <div style={{ fontStyle: 'italic', color: isDark ? '#bdbdbd' : '#555' }}>Select an entry to view its snapshot.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    {skuOptionsModal.open && (
      <div className="kitting-modal__options-backdrop" onClick={closeSkuOptionsModal}>
        <div className="kitting-modal__options-modal" onClick={e => e.stopPropagation()}>
          <header className="kitting-modal__options-header">
            <div>
              <strong>View SKU Options</strong>
              {activeComponentLabel && <div className="kitting-modal__options-subtitle">{activeComponentLabel}</div>}
            </div>
            <button type="button" className="kitting-modal__button-secondary" onClick={closeSkuOptionsModal}>Close</button>
          </header>
          <div className="kitting-modal__options-body">
            <div className="kitting-modal__options-message kitting-modal__options-message--info">
              {canModify
                ? 'Adjust default and allowed flags below. Use the Manage SKU modal to add or remove options.'
                : 'View-only snapshot. To add or edit options, use the Manage SKU modal.'}
            </div>
            {skuOptionsLoading && (
              <div className="kitting-modal__options-message kitting-modal__options-message--loading">
                Loading OrderWise details…
              </div>
            )}
            {skuOptionsError && (
              <div className="kitting-modal__options-message kitting-modal__options-message--error">{skuOptionsError}</div>
            )}
            {activeOptions.length === 0 && !skuOptionsLoading && (
              <div className="kitting-modal__options-empty">
                No SKU options stored. Use the Manage SKU modal to add or edit options.
              </div>
            )}
            {activeOptions.length > 0 && (
              <div className="kitting-modal__options-grid">
                <div className="kitting-modal__options-row kitting-modal__options-row--head">
                  <span>SKU</span>
                  <span>Description</span>
                  <span>UOM</span>
                  <span>Price</span>
                  <span>Default</span>
                  <span>Allowed</span>
                </div>
                {activeOptions.map((opt, optIdx) => {
                  const isAllowed = opt.IsAllowed !== false;
                  const isDefault = !!opt.IsDefault;
                  const defaultToggleClasses = [
                    'kitting-modal__options-toggle',
                    isDefault ? 'is-active' : '',
                    !canModify ? 'is-disabled' : ''
                  ].filter(Boolean).join(' ');
                  const allowedDisabled = !canModify || isDefault;
                  const allowedToggleClasses = [
                    'kitting-modal__options-toggle',
                    isAllowed ? 'is-active' : '',
                    allowedDisabled ? 'is-disabled' : ''
                  ].filter(Boolean).join(' ');
                  return (
                    <div key={optIdx} className="kitting-modal__options-row">
                      <span className="kitting-modal__options-value">{opt.SKU || '—'}</span>
                      <span className="kitting-modal__options-value">{opt.Description || '—'}</span>
                      <span className="kitting-modal__options-value">{opt.UOM || '—'}</span>
                      <span className="kitting-modal__options-value kitting-modal__options-price">{formatPrice(opt.Price)}</span>
                      <label
                        className={defaultToggleClasses}
                        title={isDefault ? 'This SKU is currently the default option.' : 'Set this SKU as the default.'}
                      >
                        <input
                          type="radio"
                          name={defaultRadioName}
                          checked={isDefault}
                          disabled={!canModify}
                          onChange={() => handleSetSkuOptionDefault(skuOptionsModal.componentIndex, optIdx)}
                        />
                        <span>{isDefault ? 'Default' : 'Make Default'}</span>
                      </label>
                      <label
                        className={allowedToggleClasses}
                        title={isDefault ? 'Default SKU must remain allowed.' : isAllowed ? 'Block this SKU for this kit.' : 'Allow this SKU for this kit.'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllowed}
                          disabled={allowedDisabled}
                          onChange={(e) => handleToggleSkuOptionAllowed(skuOptionsModal.componentIndex, optIdx, e.target.checked)}
                        />
                        <span>{isAllowed ? 'Allowed' : 'Blocked'}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    {refreshImporterKitIds && (
      // Wrapper lifts the importer (z-index 1200) above the kit modal backdrop (4100).
      <div style={{ position: 'relative', zIndex: 4300 }}>
        <KitImporter
          open
          onClose={() => setRefreshImporterKitId(null)}
          isDark={isDark}
          refreshKitIds={refreshImporterKitIds}
        />
      </div>
    )}
    </>
  );
}
